import React, { useState, useEffect, useMemo } from 'react';
import { MasterDataPackage, StatutoryActorMaster, ComplianceDomainMaster, CommencementTrancheMaster } from '../types/masterData';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { ArchitectureSystem, SystemType } from '../types/system';
import { ApplicabilityDecision, WorkspaceProfile } from '../types/decision';
import { ComplianceUser } from '../types/user';
import { StatutoryExceptionRecord, ExceptionCategory } from '../types/exception';
import { getGovernmentLinkage } from '../utils/governmentDocumentLookup';
import {
  evaluateEntityScopeApplicability,
  ScopeEvaluationReport,
  ScopeEvaluationItem,
  ScopeCategory,
  evaluateExceptionsApplicability,
  ExceptionsEvaluationReport,
  ExceptionEvaluationItem
} from '../utils/entityScopeEvaluator';
import {
  X,
  Settings,
  Users,
  Layers,
  Calendar,
  UserCheck,
  Scale,
  FileText,
  RotateCcw,
  Plus,
  Edit2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Save,
  Search,
  BookOpen,
  Cpu,
  Sparkles,
  ShieldCheck,
  ChevronRight,
  Sliders,
  Check,
  Landmark,
  ExternalLink,
  Database,
  Server,
  Cloud,
  Lock,
  ArrowLeft,
  CheckSquare,
  Square,
  Filter,
  Tag,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Building,
  User,
  ShieldAlert,
  Shield,
  AlertTriangle,
  HelpCircle,
  Zap,
  Award,
  Globe,
  FileSignature
} from 'lucide-react';

interface MasterDataSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: SettingsTab;
  masterData: MasterDataPackage;
  onSaveMasterData: (newData: MasterDataPackage) => void;
  onResetToDefaults: () => void;
  systems: ArchitectureSystem[];
  decisions: Record<string, ApplicabilityDecision>;
  workspace: WorkspaceProfile;
  onUpdateWorkspace: (ws: WorkspaceProfile) => void;
  onSaveSystem: (system: ArchitectureSystem) => void;
  onDeleteSystem: (systemId: string) => void;
  onUpdateObligationSystems: (obligationId: string, systemIds: string[]) => void;
  onBulkUpdateSystemObligations: (systemId: string, obligationIds: string[]) => void;
  onBulkApplyException?: (exception: StatutoryExceptionRecord) => void;
  onAutoApplyEntityScope?: (evaluatedDecisions: Record<string, ApplicabilityDecision>) => void;
}

const SYSTEM_TYPES: SystemType[] = [
  'Web / Mobile Application',
  'Microservice / Backend API',
  'Relational Database / Storage',
  'Data Warehouse / Analytics Lake',
  'Identity & Access Provider (IdP)',
  'Third-Party SaaS / Processor',
  'API Gateway / Cloud Edge',
  'Message Queue / Event Bus'
];

type SettingsTab =
  | 'overview'
  | 'entity'
  | 'exceptions'
  | 'users'
  | 'actors'
  | 'domains'
  | 'tranches'
  | 'owners'
  | 'controls'
  | 'provisions'
  | 'systems'
  | 'reset';

export default function MasterDataSettingsModal({
  isOpen,
  onClose,
  defaultTab = 'overview',
  masterData,
  onSaveMasterData,
  onResetToDefaults,
  systems,
  decisions,
  workspace,
  onUpdateWorkspace,
  onSaveSystem,
  onDeleteSystem,
  onUpdateObligationSystems,
  onBulkUpdateSystemObligations,
  onBulkApplyException,
  onAutoApplyEntityScope
}: MasterDataSettingsModalProps) {
  if (!isOpen) return null;

  const [activeTab, setActiveTab] = useState<SettingsTab>(defaultTab);

  useEffect(() => {
    if (defaultTab && isOpen) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, isOpen]);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');

  // Editing state for generic items
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const showSaveSuccess = (msg: string) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(null), 2500);
  };

  // -------------------------------------------------------------
  // CUSTOM ENTERPRISE SYSTEMS & OBLIGATION MAPPING STATE
  // -------------------------------------------------------------
  const [mappingSystem, setMappingSystem] = useState<ArchitectureSystem | null>(null);
  const [selectedObligationIds, setSelectedObligationIds] = useState<string[]>([]);
  const [mappingDomainFilter, setMappingDomainFilter] = useState<string>('all');
  const [mappingSearch, setMappingSearch] = useState<string>('');
  const [systemSearch, setSystemSearch] = useState<string>('');
  const [systemTypeFilter, setSystemTypeFilter] = useState<string>('all');

  const [isSystemFormOpen, setIsSystemFormOpen] = useState(false);
  const [sysIdInput, setSysIdInput] = useState('');
  const [sysNameInput, setSysNameInput] = useState('');
  const [sysTypeInput, setSysTypeInput] = useState<SystemType>('Web / Mobile Application');
  const [sysOwnerInput, setSysOwnerInput] = useState('');
  const [sysDescInput, setSysDescInput] = useState('');
  const [sysDataCatInput, setSysDataCatInput] = useState('');
  const [editingSystemId, setEditingSystemId] = useState<string | null>(null);

  // Helper: Get all mapped obligations for a system ID
  const getMappedObligationsForSystem = (sysId: string): string[] => {
    const matched: string[] = [];
    masterData.controls.forEach(c => {
      const d = decisions[c.id];
      if (d?.systems_in_scope && d.systems_in_scope.length > 0) {
        if (d.systems_in_scope.includes(sysId)) matched.push(c.id);
      } else if ((c.systems || '').includes(sysId)) {
        matched.push(c.id);
      }
    });
    masterData.provisions.forEach(p => {
      const d = decisions[p.id];
      if (d?.systems_in_scope && d.systems_in_scope.includes(sysId)) {
        matched.push(p.id);
      }
    });
    return matched;
  };

  const handleStartCreateSystem = () => {
    const nextNum = systems.length + 1;
    setSysIdInput(`SYS-${String(nextNum).padStart(3, '0')}`);
    setSysNameInput('');
    setSysTypeInput('Web / Mobile Application');
    setSysOwnerInput('Platform Engineering');
    setSysDescInput('');
    setSysDataCatInput('Digital Personal Data');
    setEditingSystemId(null);
    setIsSystemFormOpen(true);
  };

  const handleStartEditSystem = (sys: ArchitectureSystem) => {
    setSysIdInput(sys.id);
    setSysNameInput(sys.name);
    setSysTypeInput(sys.type);
    setSysOwnerInput(sys.owner || '');
    setSysDescInput(sys.description);
    setSysDataCatInput((sys.data_categories || []).join(', '));
    setEditingSystemId(sys.id);
    setIsSystemFormOpen(true);
  };

  const handleSaveSystemForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sysIdInput || !sysNameInput) return;

    const newSys: ArchitectureSystem = {
      id: sysIdInput.trim().toUpperCase(),
      name: sysNameInput.trim(),
      type: sysTypeInput,
      owner: sysOwnerInput.trim() || 'Unassigned',
      description: sysDescInput.trim() || 'Enterprise software component',
      data_categories: sysDataCatInput.split(',').map(s => s.trim()).filter(Boolean),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    onSaveSystem(newSys);
    setIsSystemFormOpen(false);
    showSaveSuccess(`System "${newSys.name}" saved`);
  };

  const handleDeleteSystemClick = (sysId: string, sysName: string) => {
    if (confirm(`Delete system "${sysName}" (${sysId})? Mapped obligations will be unlinked.`)) {
      onDeleteSystem(sysId);
      showSaveSuccess(`System "${sysName}" removed`);
    }
  };

  // Obligation Mapper Handlers
  const handleOpenMapper = (sys: ArchitectureSystem) => {
    setMappingSystem(sys);
    setSelectedObligationIds(getMappedObligationsForSystem(sys.id));
    setMappingSearch('');
    setMappingDomainFilter('all');
  };

  const handleToggleObligationInMapper = (oblId: string) => {
    setSelectedObligationIds(prev =>
      prev.includes(oblId) ? prev.filter(id => id !== oblId) : [...prev, oblId]
    );
  };

  const handleSaveMapper = () => {
    if (!mappingSystem) return;
    onBulkUpdateSystemObligations(mappingSystem.id, selectedObligationIds);
    showSaveSuccess(`Saved ${selectedObligationIds.length} mapped obligations for ${mappingSystem.name}`);
    setMappingSystem(null);
  };

  const handleSelectAllFiltered = (filteredIds: string[]) => {
    setSelectedObligationIds(prev => Array.from(new Set([...prev, ...filteredIds])));
  };

  const handleDeselectAll = () => {
    setSelectedObligationIds([]);
  };

  const getTypeIcon = (type: SystemType) => {
    switch (type) {
      case 'Relational Database / Storage':
      case 'Data Warehouse / Analytics Lake':
        return <Database className="w-4 h-4 text-emerald-500" />;
      case 'Identity & Access Provider (IdP)':
        return <Lock className="w-4 h-4 text-purple-500" />;
      case 'Third-Party SaaS / Processor':
        return <Cloud className="w-4 h-4 text-sky-500" />;
      default:
        return <Server className="w-4 h-4 text-indigo-500" />;
    }
  };

  // -------------------------------------------------------------
  // ACTOR HANDLERS
  // -------------------------------------------------------------
  const handleSaveActor = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = [...masterData.actors];
    const idx = updated.findIndex(a => a.id === editingItem.id);
    if (idx >= 0) {
      updated[idx] = editingItem;
    } else {
      updated.push({ ...editingItem, id: `ACTOR-${Date.now()}` });
    }
    onSaveMasterData({ ...masterData, actors: updated });
    setIsFormOpen(false);
    showSaveSuccess('Statutory actor saved');
  };

  const handleDeleteActor = (id: string) => {
    if (confirm('Delete this statutory actor?')) {
      const updated = masterData.actors.filter(a => a.id !== id);
      onSaveMasterData({ ...masterData, actors: updated });
      showSaveSuccess('Statutory actor removed');
    }
  };

  // -------------------------------------------------------------
  // DOMAIN HANDLERS
  // -------------------------------------------------------------
  const handleSaveDomain = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = [...masterData.domains];
    const idx = updated.findIndex(d => d.id === editingItem.id);
    if (idx >= 0) {
      updated[idx] = editingItem;
    } else {
      updated.push({ ...editingItem, id: `DOM-${Date.now()}` });
    }
    onSaveMasterData({ ...masterData, domains: updated });
    setIsFormOpen(false);
    showSaveSuccess('Compliance domain saved');
  };

  const handleDeleteDomain = (id: string) => {
    if (confirm('Delete this compliance domain?')) {
      const updated = masterData.domains.filter(d => d.id !== id);
      onSaveMasterData({ ...masterData, domains: updated });
      showSaveSuccess('Compliance domain removed');
    }
  };

  // -------------------------------------------------------------
  // TRANCHE HANDLERS
  // -------------------------------------------------------------
  const handleSaveTranche = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = [...masterData.tranches];
    const idx = updated.findIndex(t => t.id === editingItem.id);
    if (idx >= 0) {
      updated[idx] = editingItem;
    } else {
      updated.push(editingItem);
    }
    onSaveMasterData({ ...masterData, tranches: updated });
    setIsFormOpen(false);
    showSaveSuccess('Commencement tranche saved');
  };

  const handleDeleteTranche = (id: string) => {
    if (confirm('Delete this commencement tranche?')) {
      const updated = masterData.tranches.filter(t => t.id !== id);
      onSaveMasterData({ ...masterData, tranches: updated });
      showSaveSuccess('Commencement tranche removed');
    }
  };

  // -------------------------------------------------------------
  // OWNER HANDLERS
  // -------------------------------------------------------------
  const handleAddOwner = (ownerName: string) => {
    if (!ownerName || masterData.owners.includes(ownerName)) return;
    const updated = [...masterData.owners, ownerName.trim()];
    onSaveMasterData({ ...masterData, owners: updated });
    showSaveSuccess('Control owner added');
  };

  const handleDeleteOwner = (ownerName: string) => {
    if (confirm(`Remove owner "${ownerName}"?`)) {
      const updated = masterData.owners.filter(o => o !== ownerName);
      onSaveMasterData({ ...masterData, owners: updated });
      showSaveSuccess('Control owner removed');
    }
  };

  // -------------------------------------------------------------
  // CONTROL MATRIX HANDLERS
  // -------------------------------------------------------------
  const handleSaveControl = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = [...masterData.controls];
    const idx = updated.findIndex(c => c.id === editingItem.id);
    if (idx >= 0) {
      updated[idx] = editingItem;
    } else {
      updated.push(editingItem);
    }
    onSaveMasterData({ ...masterData, controls: updated });

    if (editingItem?.systems) {
      const sysIds = editingItem.systems.split(';').map((s: string) => s.trim()).filter(Boolean);
      onUpdateObligationSystems(editingItem.id, sysIds);
    }

    setIsFormOpen(false);
    showSaveSuccess('Control obligation saved to matrix');
  };

  const handleDeleteControl = (id: string) => {
    if (confirm(`Delete control obligation "${id}" from matrix?`)) {
      const updated = masterData.controls.filter(c => c.id !== id);
      onSaveMasterData({ ...masterData, controls: updated });
      showSaveSuccess('Control removed from matrix');
    }
  };

  // -------------------------------------------------------------
  // STATUTORY PROVISION HANDLERS
  // -------------------------------------------------------------
  const handleSaveProvision = (e: React.FormEvent) => {
    e.preventDefault();
    const updated = [...masterData.provisions];
    const idx = updated.findIndex(p => p.id === editingItem.id);
    if (idx >= 0) {
      updated[idx] = editingItem;
    } else {
      updated.push(editingItem);
    }
    onSaveMasterData({ ...masterData, provisions: updated });
    setIsFormOpen(false);
    showSaveSuccess('Statutory provision saved');
  };

  const handleDeleteProvision = (id: string) => {
    if (confirm(`Delete statutory provision "${id}"?`)) {
      const updated = masterData.provisions.filter(p => p.id !== id);
      onSaveMasterData({ ...masterData, provisions: updated });
      showSaveSuccess('Statutory provision removed');
    }
  };

  // -------------------------------------------------------------
  // PERSONNEL & USER HANDLERS
  // -------------------------------------------------------------
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [userFormId, setUserFormId] = useState('');
  const [userFormFirstName, setUserFormFirstName] = useState('');
  const [userFormLastName, setUserFormLastName] = useState('');
  const [userFormDesignation, setUserFormDesignation] = useState('');
  const [userFormEmail, setUserFormEmail] = useState('');
  const [userFormPhone, setUserFormPhone] = useState('');
  const [userFormDepartment, setUserFormDepartment] = useState('');
  const [userFormMappedRoles, setUserFormMappedRoles] = useState<string[]>([]);
  const [userFormNotes, setUserFormNotes] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userDeptFilter, setUserDeptFilter] = useState('all');

  const handleStartCreateUser = () => {
    const existingUsers = masterData.users || [];
    const nextNum = existingUsers.length + 1;
    setUserFormId(`USR-${String(nextNum).padStart(3, '0')}`);
    setUserFormFirstName('');
    setUserFormLastName('');
    setUserFormDesignation('');
    setUserFormEmail('');
    setUserFormPhone('');
    setUserFormDepartment('Legal & Compliance');
    setUserFormMappedRoles([]);
    setUserFormNotes('');
    setEditingUserId(null);
    setIsUserFormOpen(true);
  };

  const handleStartEditUser = (user: ComplianceUser) => {
    setUserFormId(user.id);
    setUserFormFirstName(user.first_name);
    setUserFormLastName(user.last_name);
    setUserFormDesignation(user.designation);
    setUserFormEmail(user.email);
    setUserFormPhone(user.phone);
    setUserFormDepartment(user.department || 'Legal & Compliance');
    setUserFormMappedRoles(user.mapped_roles || []);
    setUserFormNotes(user.notes || '');
    setEditingUserId(user.id);
    setIsUserFormOpen(true);
  };

  const handleToggleUserMappedRole = (role: string) => {
    setUserFormMappedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSaveUserForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userFormFirstName.trim() || !userFormLastName.trim() || !userFormEmail.trim()) {
      alert('Please provide first name, last name, and contact email.');
      return;
    }

    const newUser: ComplianceUser = {
      id: userFormId.trim() || `USR-${Date.now()}`,
      first_name: userFormFirstName.trim(),
      last_name: userFormLastName.trim(),
      designation: userFormDesignation.trim() || 'Compliance Lead',
      email: userFormEmail.trim(),
      phone: userFormPhone.trim(),
      department: userFormDepartment.trim() || 'General Compliance',
      mapped_roles: userFormMappedRoles,
      notes: userFormNotes.trim(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const existingUsers = masterData.users || [];
    let updatedUsers: ComplianceUser[];
    const idx = existingUsers.findIndex(u => u.id === (editingUserId || newUser.id));
    if (idx >= 0) {
      updatedUsers = [...existingUsers];
      updatedUsers[idx] = { ...existingUsers[idx], ...newUser, updated_at: new Date().toISOString() };
    } else {
      updatedUsers = [...existingUsers, newUser];
    }

    onSaveMasterData({ ...masterData, users: updatedUsers });
    setIsUserFormOpen(false);
    showSaveSuccess(`Personnel "${newUser.first_name} ${newUser.last_name}" saved`);
  };

  const handleDeleteUser = (userId: string, userName: string) => {
    if (confirm(`Remove personnel "${userName}" (${userId})?`)) {
      const existingUsers = masterData.users || [];
      const updatedUsers = existingUsers.filter(u => u.id !== userId);
      onSaveMasterData({ ...masterData, users: updatedUsers });
      showSaveSuccess(`Personnel "${userName}" removed`);
    }
  };

  const departmentOptions = useMemo(() => {
    const defaultDepts = [
      'Legal & Compliance',
      'Information Security',
      'Platform Engineering',
      'IT Operations',
      'Procurement & TPRM',
      'Human Resources',
      'Marketing Operations'
    ];
    const existing = (masterData.users || []).map(u => u.department).filter(Boolean) as string[];
    return Array.from(new Set([...defaultDepts, ...existing]));
  }, [masterData.users]);

  const filteredUsers = useMemo(() => {
    return (masterData.users || []).filter(u => {
      const q = userSearch.toLowerCase();
      const matchesSearch =
        !q ||
        u.first_name.toLowerCase().includes(q) ||
        u.last_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.phone.toLowerCase().includes(q) ||
        u.designation.toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q) ||
        (u.mapped_roles || []).some(r => r.toLowerCase().includes(q));

      const matchesDept = userDeptFilter === 'all' || u.department === userDeptFilter;
      const matchesRole = userRoleFilter === 'all' || (u.mapped_roles || []).includes(userRoleFilter);

      return matchesSearch && matchesDept && matchesRole;
    });
  }, [masterData.users, userSearch, userDeptFilter, userRoleFilter]);

  // -------------------------------------------------------------
  // LEGAL ENTITY PROFILE & REGULATORY SCOPE STATE
  // -------------------------------------------------------------
  const [entityNameInput, setEntityNameInput] = useState(workspace?.entity_name || '');
  const [tradeBrandNameInput, setTradeBrandNameInput] = useState(workspace?.trade_brand_name || '');
  const [entityTypeInput, setEntityTypeInput] = useState(workspace?.entity_type || 'Private Limited Company');
  const [cinInput, setCinInput] = useState(workspace?.cin_registration || '');
  const [panTanInput, setPanTanInput] = useState(workspace?.pan_tan || '');
  const [gstinInput, setGstinInput] = useState(workspace?.gstin || '');
  const [industryInput, setIndustryInput] = useState(workspace?.industry_sector || 'Fintech & Financial Services (BFSI)');
  const [incorporationDateInput, setIncorporationDateInput] = useState(workspace?.incorporation_date || '');
  const [addressInput, setAddressInput] = useState(workspace?.registered_address || '');
  const [headquartersAddressInput, setHeadquartersAddressInput] = useState(workspace?.headquarters_address || '');
  const [websiteInput, setWebsiteInput] = useState(workspace?.corporate_website || '');
  const [corporateEmailInput, setCorporateEmailInput] = useState(workspace?.corporate_email || '');
  const [corporatePhoneInput, setCorporatePhoneInput] = useState(workspace?.corporate_phone || '');

  // Governance Officers
  const [dpoNameInput, setDpoNameInput] = useState(workspace?.dpo_name || '');
  const [dpoDesignationInput, setDpoDesignationInput] = useState(workspace?.dpo_designation || '');
  const [dpoEmailInput, setDpoEmailInput] = useState(workspace?.dpo_email || '');
  const [dpoPhoneInput, setDpoPhoneInput] = useState(workspace?.dpo_phone || '');
  const [dpoOfficeAddressInput, setDpoOfficeAddressInput] = useState(workspace?.dpo_office_address || '');

  const [grievanceOfficerNameInput, setGrievanceOfficerNameInput] = useState(workspace?.grievance_officer_name || '');
  const [grievanceOfficerDesignationInput, setGrievanceOfficerDesignationInput] = useState(workspace?.grievance_officer_designation || '');
  const [grievanceOfficerEmailInput, setGrievanceOfficerEmailInput] = useState(workspace?.grievance_officer_email || '');
  const [grievanceOfficerPhoneInput, setGrievanceOfficerPhoneInput] = useState(workspace?.grievance_officer_phone || '');
  const [grievancePortalUrlInput, setGrievancePortalUrlInput] = useState(workspace?.grievance_portal_url || '');

  const [authSignatoryNameInput, setAuthSignatoryNameInput] = useState(workspace?.authorized_signatory_name || '');
  const [authSignatoryDesignationInput, setAuthSignatoryDesignationInput] = useState(workspace?.authorized_signatory_designation || '');
  const [authSignatoryEmailInput, setAuthSignatoryEmailInput] = useState(workspace?.authorized_signatory_email || '');

  // Regulatory Scope
  const [isSdfInput, setIsSdfInput] = useState(workspace?.is_sdf || false);
  const [sdfFactorsInput, setSdfFactorsInput] = useState<string[]>(workspace?.sdf_factors || []);
  const [sdfGazetteInput, setSdfGazetteInput] = useState(workspace?.sdf_gazette_notification || '');
  const [processesChildrenInput, setProcessesChildrenInput] = useState(workspace?.processes_children_data || false);
  const [parentalMechanismInput, setParentalMechanismInput] = useState(workspace?.parental_consent_mechanism || '');
  const [ageGatingMechanismInput, setAgeGatingMechanismInput] = useState(workspace?.age_gating_mechanism || '');
  const [processesDisabilitiesInput, setProcessesDisabilitiesInput] = useState(workspace?.processes_disabilities_data || false);
  const [guardianMechanismInput, setGuardianMechanismInput] = useState(workspace?.guardian_consent_mechanism || '');
  const [hasCrossBorderInput, setHasCrossBorderInput] = useState(workspace?.has_cross_border_transfers || false);
  const [crossBorderSafeguardsInput, setCrossBorderSafeguardsInput] = useState(workspace?.cross_border_safeguards || '');
  const [crossBorderCountriesInput, setCrossBorderCountriesInput] = useState(workspace?.cross_border_countries || '');
  const [usesConsentManagersInput, setUsesConsentManagersInput] = useState(workspace?.uses_consent_managers || false);
  const [consentManagerProvidersInput, setConsentManagerProvidersInput] = useState(workspace?.consent_manager_providers || '');

  // Processing Scale
  const [estimatedPrincipalsInput, setEstimatedPrincipalsInput] = useState(workspace?.estimated_data_principals || '');
  const [principalCategoriesInput, setPrincipalCategoriesInput] = useState<string>((workspace?.data_principal_categories || []).join(', '));
  const [processorsCountInput, setProcessorsCountInput] = useState<number>(workspace?.total_data_processors_count || 0);

  useEffect(() => {
    if (workspace) {
      setEntityNameInput(workspace.entity_name || '');
      setTradeBrandNameInput(workspace.trade_brand_name || '');
      setEntityTypeInput(workspace.entity_type || 'Private Limited Company');
      setCinInput(workspace.cin_registration || '');
      setPanTanInput(workspace.pan_tan || '');
      setGstinInput(workspace.gstin || '');
      setIndustryInput(workspace.industry_sector || 'Fintech & Financial Services (BFSI)');
      setIncorporationDateInput(workspace.incorporation_date || '');
      setAddressInput(workspace.registered_address || '');
      setHeadquartersAddressInput(workspace.headquarters_address || '');
      setWebsiteInput(workspace.corporate_website || '');
      setCorporateEmailInput(workspace.corporate_email || '');
      setCorporatePhoneInput(workspace.corporate_phone || '');

      setDpoNameInput(workspace.dpo_name || '');
      setDpoDesignationInput(workspace.dpo_designation || '');
      setDpoEmailInput(workspace.dpo_email || '');
      setDpoPhoneInput(workspace.dpo_phone || '');
      setDpoOfficeAddressInput(workspace.dpo_office_address || '');

      setGrievanceOfficerNameInput(workspace.grievance_officer_name || '');
      setGrievanceOfficerDesignationInput(workspace.grievance_officer_designation || '');
      setGrievanceOfficerEmailInput(workspace.grievance_officer_email || '');
      setGrievanceOfficerPhoneInput(workspace.grievance_officer_phone || '');
      setGrievancePortalUrlInput(workspace.grievance_portal_url || '');

      setAuthSignatoryNameInput(workspace.authorized_signatory_name || '');
      setAuthSignatoryDesignationInput(workspace.authorized_signatory_designation || '');
      setAuthSignatoryEmailInput(workspace.authorized_signatory_email || '');

      setIsSdfInput(workspace.is_sdf || false);
      setSdfFactorsInput(workspace.sdf_factors || []);
      setSdfGazetteInput(workspace.sdf_gazette_notification || '');
      setProcessesChildrenInput(workspace.processes_children_data || false);
      setParentalMechanismInput(workspace.parental_consent_mechanism || '');
      setAgeGatingMechanismInput(workspace.age_gating_mechanism || '');
      setProcessesDisabilitiesInput(workspace.processes_disabilities_data || false);
      setGuardianMechanismInput(workspace.guardian_consent_mechanism || '');
      setHasCrossBorderInput(workspace.has_cross_border_transfers || false);
      setCrossBorderSafeguardsInput(workspace.cross_border_safeguards || '');
      setCrossBorderCountriesInput(workspace.cross_border_countries || '');
      setUsesConsentManagersInput(workspace.uses_consent_managers || false);
      setConsentManagerProvidersInput(workspace.consent_manager_providers || '');

      setEstimatedPrincipalsInput(workspace.estimated_data_principals || '');
      setPrincipalCategoriesInput((workspace.data_principal_categories || []).join(', '));
      setProcessorsCountInput(workspace.total_data_processors_count || 0);
    }
  }, [workspace, isOpen]);

  const handleSaveEntityProfile = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedWs: WorkspaceProfile = {
      ...workspace,
      entity_name: entityNameInput.trim() || 'Enterprise Data Fiduciary',
      trade_brand_name: tradeBrandNameInput.trim(),
      entity_type: entityTypeInput,
      cin_registration: cinInput.trim(),
      pan_tan: panTanInput.trim(),
      gstin: gstinInput.trim(),
      industry_sector: industryInput,
      incorporation_date: incorporationDateInput.trim(),
      registered_address: addressInput.trim(),
      headquarters_address: headquartersAddressInput.trim(),
      corporate_website: websiteInput.trim(),
      corporate_email: corporateEmailInput.trim(),
      corporate_phone: corporatePhoneInput.trim(),

      dpo_name: dpoNameInput.trim(),
      dpo_designation: dpoDesignationInput.trim(),
      dpo_email: dpoEmailInput.trim(),
      dpo_phone: dpoPhoneInput.trim(),
      dpo_office_address: dpoOfficeAddressInput.trim(),

      grievance_officer_name: grievanceOfficerNameInput.trim(),
      grievance_officer_designation: grievanceOfficerDesignationInput.trim(),
      grievance_officer_email: grievanceOfficerEmailInput.trim(),
      grievance_officer_phone: grievanceOfficerPhoneInput.trim(),
      grievance_portal_url: grievancePortalUrlInput.trim(),

      authorized_signatory_name: authSignatoryNameInput.trim(),
      authorized_signatory_designation: authSignatoryDesignationInput.trim(),
      authorized_signatory_email: authSignatoryEmailInput.trim(),

      is_sdf: isSdfInput,
      sdf_factors: sdfFactorsInput,
      sdf_gazette_notification: sdfGazetteInput.trim(),
      processes_children_data: processesChildrenInput,
      parental_consent_mechanism: parentalMechanismInput.trim(),
      age_gating_mechanism: ageGatingMechanismInput.trim(),
      processes_disabilities_data: processesDisabilitiesInput,
      guardian_consent_mechanism: guardianMechanismInput.trim(),
      has_cross_border_transfers: hasCrossBorderInput,
      cross_border_safeguards: crossBorderSafeguardsInput.trim(),
      cross_border_countries: crossBorderCountriesInput.trim(),
      uses_consent_managers: usesConsentManagersInput,
      consent_manager_providers: consentManagerProvidersInput.trim(),

      estimated_data_principals: estimatedPrincipalsInput.trim(),
      data_principal_categories: principalCategoriesInput.split(',').map(s => s.trim()).filter(Boolean),
      total_data_processors_count: Number(processorsCountInput) || 0,
      updated_at: new Date().toISOString()
    };
    onUpdateWorkspace(updatedWs);
    showSaveSuccess('Company entity details & regulatory scope saved');
  };

  const handleToggleSdfFactor = (factor: string) => {
    setSdfFactorsInput(prev =>
      prev.includes(factor) ? prev.filter(f => f !== factor) : [...prev, factor]
    );
  };

  // -------------------------------------------------------------
  // STATUTORY SCOPE EVALUATION & AUTO-APPLY STATE
  // -------------------------------------------------------------
  const [isScopeReviewOpen, setIsScopeReviewOpen] = useState(false);
  const [scopeEvaluationReport, setScopeEvaluationReport] = useState<ScopeEvaluationReport | null>(null);
  const [scopeFilterStatus, setScopeFilterStatus] = useState<'all' | 'applicable' | 'not_applicable' | 'changed'>('all');
  const [scopeFilterCategory, setScopeFilterCategory] = useState<string>('all');
  const [scopeFilterTargetType, setScopeFilterTargetType] = useState<'all' | 'control' | 'provision'>('all');
  const [scopeSearchQuery, setScopeSearchQuery] = useState('');

  const getCurrentWorkspaceFromInputs = (): WorkspaceProfile => {
    return {
      ...workspace,
      entity_name: entityNameInput.trim() || 'Enterprise Data Fiduciary',
      trade_brand_name: tradeBrandNameInput.trim(),
      entity_type: entityTypeInput,
      cin_registration: cinInput.trim(),
      pan_tan: panTanInput.trim(),
      gstin: gstinInput.trim(),
      industry_sector: industryInput,
      incorporation_date: incorporationDateInput.trim(),
      registered_address: addressInput.trim(),
      headquarters_address: headquartersAddressInput.trim(),
      corporate_website: websiteInput.trim(),
      corporate_email: corporateEmailInput.trim(),
      corporate_phone: corporatePhoneInput.trim(),

      dpo_name: dpoNameInput.trim(),
      dpo_designation: dpoDesignationInput.trim(),
      dpo_email: dpoEmailInput.trim(),
      dpo_phone: dpoPhoneInput.trim(),
      dpo_office_address: dpoOfficeAddressInput.trim(),

      grievance_officer_name: grievanceOfficerNameInput.trim(),
      grievance_officer_designation: grievanceOfficerDesignationInput.trim(),
      grievance_officer_email: grievanceOfficerEmailInput.trim(),
      grievance_officer_phone: grievanceOfficerPhoneInput.trim(),
      grievance_portal_url: grievancePortalUrlInput.trim(),

      authorized_signatory_name: authSignatoryNameInput.trim(),
      authorized_signatory_designation: authSignatoryDesignationInput.trim(),
      authorized_signatory_email: authSignatoryEmailInput.trim(),

      is_sdf: isSdfInput,
      sdf_factors: sdfFactorsInput,
      sdf_gazette_notification: sdfGazetteInput.trim(),
      processes_children_data: processesChildrenInput,
      parental_consent_mechanism: parentalMechanismInput.trim(),
      age_gating_mechanism: ageGatingMechanismInput.trim(),
      processes_disabilities_data: processesDisabilitiesInput,
      guardian_consent_mechanism: guardianMechanismInput.trim(),
      has_cross_border_transfers: hasCrossBorderInput,
      cross_border_safeguards: crossBorderSafeguardsInput.trim(),
      cross_border_countries: crossBorderCountriesInput.trim(),
      uses_consent_managers: usesConsentManagersInput,
      consent_manager_providers: consentManagerProvidersInput.trim(),

      estimated_data_principals: estimatedPrincipalsInput.trim(),
      data_principal_categories: principalCategoriesInput.split(',').map(s => s.trim()).filter(Boolean),
      total_data_processors_count: Number(processorsCountInput) || 0,
      updated_at: new Date().toISOString()
    };
  };

  const handleOpenScopeEvaluation = () => {
    const currentWs = getCurrentWorkspaceFromInputs();
    const report = evaluateEntityScopeApplicability(
      currentWs,
      masterData.controls,
      masterData.provisions,
      decisions,
      masterData.exceptions
    );
    setScopeEvaluationReport(report);
    setScopeFilterStatus('all');
    setScopeFilterCategory('all');
    setScopeFilterTargetType('all');
    setScopeSearchQuery('');
    setIsScopeReviewOpen(true);
  };

  const handleConfirmApplyScopeRules = () => {
    if (!scopeEvaluationReport) return;
    const currentWs = getCurrentWorkspaceFromInputs();
    onUpdateWorkspace(currentWs);
    if (onAutoApplyEntityScope) {
      onAutoApplyEntityScope(scopeEvaluationReport.decisions_to_apply);
    }
    setIsScopeReviewOpen(false);
    showSaveSuccess(`⚡ Auto-applied statutory scope rules! ${scopeEvaluationReport.changed_count} items updated across register.`);
  };

  const filteredScopeItems = useMemo(() => {
    if (!scopeEvaluationReport) return [];
    return scopeEvaluationReport.items.filter(item => {
      if (scopeFilterTargetType !== 'all' && item.target_type !== scopeFilterTargetType) return false;
      if (scopeFilterStatus === 'changed' && !item.is_changed) return false;
      if (scopeFilterStatus === 'applicable' && item.recommended_status !== 'APPLICABLE') return false;
      if (scopeFilterStatus === 'not_applicable' && item.recommended_status !== 'NOT_APPLICABLE') return false;

      if (scopeFilterCategory !== 'all' && item.category !== scopeFilterCategory) return false;

      if (scopeSearchQuery.trim()) {
        const q = scopeSearchQuery.toLowerCase();
        return (
          item.target_id.toLowerCase().includes(q) ||
          item.title.toLowerCase().includes(q) ||
          item.statutory_ground.toLowerCase().includes(q) ||
          item.trigger_reason.toLowerCase().includes(q) ||
          item.rationale.toLowerCase().includes(q) ||
          item.domain.toLowerCase().includes(q) ||
          (item.provision_ref && item.provision_ref.toLowerCase().includes(q))
        );
      }

      return true;
    });
  }, [scopeEvaluationReport, scopeFilterTargetType, scopeFilterStatus, scopeFilterCategory, scopeSearchQuery]);

  // -------------------------------------------------------------
  // STATUTORY EXCEPTIONS & SAFE HARBOURS HANDLERS
  // -------------------------------------------------------------
  const [isExceptionFormOpen, setIsExceptionFormOpen] = useState(false);
  const [editingExceptionId, setEditingExceptionId] = useState<string | null>(null);
  const [excIdInput, setExcIdInput] = useState('');
  const [excTitleInput, setExcTitleInput] = useState('');
  const [excGroundInput, setExcGroundInput] = useState('Section 17(1)(a)');
  const [excCategoryInput, setExcCategoryInput] = useState<ExceptionCategory>('legal_claims');
  const [excStatusInput, setExcStatusInput] = useState<'ACTIVE' | 'PROVISIONAL' | 'EXPIRED' | 'UNDER_REVIEW'>('ACTIVE');
  const [excJustificationInput, setExcJustificationInput] = useState('');
  const [excMappedObligationsInput, setExcMappedObligationsInput] = useState<string[]>([]);
  const [excApprovedByInput, setExcApprovedByInput] = useState('');
  const [excApprovalDateInput, setExcApprovalDateInput] = useState(new Date().toISOString().slice(0, 10));
  const [excExpiryDateInput, setExcExpiryDateInput] = useState('');
  const [excNotesInput, setExcNotesInput] = useState('');
  const [excSearch, setExcSearch] = useState('');
  const [excCategoryFilter, setExcCategoryFilter] = useState('all');
  const [excStatusFilter, setExcStatusFilter] = useState('all');
  const [excObligationFilterDomain, setExcObligationFilterDomain] = useState('all');
  const [excObligationFilterType, setExcObligationFilterType] = useState<'all' | 'control' | 'provision'>('all');
  const [excObligationSearch, setExcObligationSearch] = useState('');

  // -------------------------------------------------------------
  // STATUTORY EXCEPTIONS EVALUATION & AUTO-APPLY STATE
  // -------------------------------------------------------------
  const [isExceptionsReviewOpen, setIsExceptionsReviewOpen] = useState(false);
  const [exceptionsEvaluationReport, setExceptionsEvaluationReport] = useState<ExceptionsEvaluationReport | null>(null);
  const [exceptionsFilterExceptionId, setExceptionsFilterExceptionId] = useState<string>('all');
  const [exceptionsFilterChangedOnly, setExceptionsFilterChangedOnly] = useState(false);
  const [exceptionsFilterTargetType, setExceptionsFilterTargetType] = useState<'all' | 'control' | 'provision'>('all');
  const [exceptionsSearchQuery, setExceptionsSearchQuery] = useState('');

  const handleOpenExceptionsEvaluation = (targetExceptionId?: string) => {
    const report = evaluateExceptionsApplicability(
      masterData.exceptions || [],
      masterData.controls,
      masterData.provisions,
      decisions,
      targetExceptionId
    );
    setExceptionsEvaluationReport(report);
    setExceptionsFilterExceptionId(targetExceptionId || 'all');
    setExceptionsFilterChangedOnly(false);
    setExceptionsFilterTargetType('all');
    setExceptionsSearchQuery('');
    setIsExceptionsReviewOpen(true);
  };

  const handleConfirmApplyExceptions = () => {
    if (!exceptionsEvaluationReport) return;
    if (onAutoApplyEntityScope) {
      onAutoApplyEntityScope(exceptionsEvaluationReport.decisions_to_apply);
    } else if (onBulkApplyException) {
      (masterData.exceptions || []).filter(e => e.status === 'ACTIVE').forEach(ex => {
        onBulkApplyException(ex);
      });
    }
    setIsExceptionsReviewOpen(false);
    showSaveSuccess(`⚡ Auto-applied statutory safe harbours to ${exceptionsEvaluationReport.total_mapped_obligations_count} items (${exceptionsEvaluationReport.changed_count} status updates)`);
  };

  const filteredExceptionItems = useMemo(() => {
    if (!exceptionsEvaluationReport) return [];
    return exceptionsEvaluationReport.items.filter(item => {
      if (exceptionsFilterTargetType !== 'all' && item.target_type !== exceptionsFilterTargetType) return false;
      if (exceptionsFilterChangedOnly && !item.is_changed) return false;
      if (exceptionsFilterExceptionId !== 'all' && item.exception_id !== exceptionsFilterExceptionId) return false;

      if (exceptionsSearchQuery.trim()) {
        const q = exceptionsSearchQuery.toLowerCase();
        return (
          item.target_id.toLowerCase().includes(q) ||
          item.title.toLowerCase().includes(q) ||
          item.exception_title.toLowerCase().includes(q) ||
          item.statutory_ground.toLowerCase().includes(q) ||
          item.justification.toLowerCase().includes(q) ||
          item.domain.toLowerCase().includes(q) ||
          (item.provision_ref && item.provision_ref.toLowerCase().includes(q))
        );
      }

      return true;
    });
  }, [exceptionsEvaluationReport, exceptionsFilterTargetType, exceptionsFilterChangedOnly, exceptionsFilterExceptionId, exceptionsSearchQuery]);

  const combinedExceptionTargetOptions = useMemo(() => {
    const ctrlItems = (masterData.controls || []).map(c => ({
      id: c.id,
      type: 'control' as const,
      title: c.title || c.name,
      subtitle: `${c.scope || c.provision || ''} • Owner: ${c.recommended_owner || 'Legal'}`,
      domain: c.domain_slug || c.domain || 'general'
    }));
    const provItems = (masterData.provisions || []).map(p => ({
      id: p.id,
      type: 'provision' as const,
      title: `${p.provision} — ${p.trigger || p.actor || p.id}`,
      subtitle: p.author_interpretation || p.conditions || 'DPDP Statutory Rule',
      domain: p.domain_slug || p.domain || 'legal'
    }));
    return [...ctrlItems, ...provItems];
  }, [masterData.controls, masterData.provisions]);

  const filteredExceptionTargetOptions = useMemo(() => {
    return combinedExceptionTargetOptions.filter(opt => {
      if (excObligationFilterType !== 'all' && opt.type !== excObligationFilterType) return false;
      if (excObligationSearch.trim()) {
        const q = excObligationSearch.toLowerCase();
        return (
          opt.id.toLowerCase().includes(q) ||
          opt.title.toLowerCase().includes(q) ||
          opt.subtitle.toLowerCase().includes(q) ||
          opt.domain.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [combinedExceptionTargetOptions, excObligationFilterType, excObligationSearch]);

  const handleStartCreateException = () => {
    const existing = masterData.exceptions || [];
    const nextNum = existing.length + 1;
    setExcIdInput(`EXC-${String(nextNum).padStart(3, '0')}`);
    setExcTitleInput('');
    setExcGroundInput('Section 17(1)(a)');
    setExcCategoryInput('legal_claims');
    setExcStatusInput('ACTIVE');
    setExcJustificationInput('');
    setExcMappedObligationsInput([]);
    setExcApprovedByInput(masterData.users?.[0] ? `${masterData.users[0].first_name} ${masterData.users[0].last_name} (${masterData.users[0].designation})` : 'Lead Privacy Counsel');
    setExcApprovalDateInput(new Date().toISOString().slice(0, 10));
    setExcExpiryDateInput('');
    setExcNotesInput('');
    setEditingExceptionId(null);
    setIsExceptionFormOpen(true);
  };

  const handleStartEditException = (ex: StatutoryExceptionRecord) => {
    setExcIdInput(ex.id);
    setExcTitleInput(ex.title);
    setExcGroundInput(ex.statutory_ground);
    setExcCategoryInput(ex.category);
    setExcStatusInput(ex.status);
    setExcJustificationInput(ex.justification);
    setExcMappedObligationsInput(ex.mapped_obligation_ids || []);
    setExcApprovedByInput(ex.approved_by);
    setExcApprovalDateInput(ex.approval_date);
    setExcExpiryDateInput(ex.expiry_date || '');
    setExcNotesInput(ex.notes || '');
    setEditingExceptionId(ex.id);
    setIsExceptionFormOpen(true);
  };

  const handleToggleExceptionMappedObligation = (oblId: string) => {
    setExcMappedObligationsInput(prev =>
      prev.includes(oblId) ? prev.filter(id => id !== oblId) : [...prev, oblId]
    );
  };

  const handleSaveExceptionForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!excTitleInput.trim() || !excJustificationInput.trim()) {
      alert('Please provide exception title and statutory justification.');
      return;
    }

    const newEx: StatutoryExceptionRecord = {
      id: excIdInput.trim() || `EXC-${Date.now()}`,
      title: excTitleInput.trim(),
      statutory_ground: excGroundInput.trim(),
      category: excCategoryInput,
      status: excStatusInput,
      justification: excJustificationInput.trim(),
      mapped_obligation_ids: excMappedObligationsInput,
      approved_by: excApprovedByInput.trim() || 'Compliance Lead',
      approval_date: excApprovalDateInput,
      expiry_date: excExpiryDateInput ? excExpiryDateInput : undefined,
      notes: excNotesInput.trim(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const existing = masterData.exceptions || [];
    let updated: StatutoryExceptionRecord[];
    const idx = existing.findIndex(ex => ex.id === (editingExceptionId || newEx.id));
    if (idx >= 0) {
      updated = [...existing];
      updated[idx] = { ...existing[idx], ...newEx, updated_at: new Date().toISOString() };
    } else {
      updated = [...existing, newEx];
    }

    onSaveMasterData({ ...masterData, exceptions: updated });
    setIsExceptionFormOpen(false);
    showSaveSuccess(`Statutory exception "${newEx.title}" saved`);
  };

  const handleDeleteException = (exId: string, exTitle: string) => {
    if (confirm(`Delete statutory exception "${exTitle}" (${exId})?`)) {
      const existing = masterData.exceptions || [];
      const updated = existing.filter(ex => ex.id !== exId);
      onSaveMasterData({ ...masterData, exceptions: updated });
      showSaveSuccess(`Statutory exception removed`);
    }
  };

  const handleApplyExceptionToDecisionsClick = (ex: StatutoryExceptionRecord) => {
    handleOpenExceptionsEvaluation(ex.id);
  };

  const filteredExceptions = useMemo(() => {
    return (masterData.exceptions || []).filter(ex => {
      const q = excSearch.toLowerCase();
      const matchesSearch =
        !q ||
        ex.title.toLowerCase().includes(q) ||
        ex.statutory_ground.toLowerCase().includes(q) ||
        ex.justification.toLowerCase().includes(q) ||
        ex.approved_by.toLowerCase().includes(q) ||
        (ex.mapped_obligation_ids || []).some(id => id.toLowerCase().includes(q));

      const matchesCat = excCategoryFilter === 'all' || ex.category === excCategoryFilter;
      const matchesStat = excStatusFilter === 'all' || ex.status === excStatusFilter;

      return matchesSearch && matchesCat && matchesStat;
    });
  }, [masterData.exceptions, excSearch, excCategoryFilter, excStatusFilter]);

  // Sidebar Menu Definition
  const menuSections = [
    {
      label: 'ORGANIZATION & ENTITY',
      items: [
        { id: 'overview' as SettingsTab, label: 'Overview & Baseline', icon: Sparkles, badge: null },
        { id: 'entity' as SettingsTab, label: 'Legal Entity & Scope', icon: Building2, badge: workspace?.is_sdf ? 'SDF' : null },
        { id: 'users' as SettingsTab, label: 'Compliance Personnel', icon: Users, badge: (masterData.users || []).length },
        { id: 'owners' as SettingsTab, label: 'Control Owner Roles', icon: UserCheck, badge: masterData.owners.length }
      ]
    },
    {
      label: 'ARCHITECTURE & SYSTEMS',
      items: [
        { id: 'systems' as SettingsTab, label: 'Custom Systems & Mapping', icon: Cpu, badge: systems.length }
      ]
    },
    {
      label: 'STATUTORY EXCEPTIONS & TAXONOMY',
      items: [
        { id: 'exceptions' as SettingsTab, label: 'Statutory Exceptions', icon: ShieldAlert, badge: (masterData.exceptions || []).length },
        { id: 'actors' as SettingsTab, label: 'Statutory Actors', icon: Landmark, badge: masterData.actors.length },
        { id: 'domains' as SettingsTab, label: 'Compliance Domains', icon: Layers, badge: masterData.domains.length },
        { id: 'tranches' as SettingsTab, label: 'Effective Tranches', icon: Calendar, badge: masterData.tranches.length }
      ]
    },
    {
      label: 'GOVERNANCE & CONTROLS',
      items: [
        { id: 'controls' as SettingsTab, label: 'Control Matrix', icon: FileText, badge: masterData.controls.length }
      ]
    },
    {
      label: 'STATUTORY PROVISIONS',
      items: [
        { id: 'provisions' as SettingsTab, label: 'Provisions & Rules', icon: Scale, badge: masterData.provisions.length }
      ]
    },
    {
      label: 'MAINTENANCE',
      items: [
        { id: 'reset' as SettingsTab, label: 'Factory Reset', icon: RotateCcw, badge: null }
      ]
    }
  ];

  // Filtered Menu Items for search
  const filteredSections = useMemo(() => {
    if (!sidebarSearch) return menuSections;
    const q = sidebarSearch.toLowerCase();
    return menuSections
      .map(sec => ({
        ...sec,
        items: sec.items.filter(it => it.label.toLowerCase().includes(q))
      }))
      .filter(sec => sec.items.length > 0);
  }, [sidebarSearch, menuSections]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-3 sm:p-6 bg-slate-950/75 backdrop-blur-xs transition-opacity">
      
      {/* Obsidian-Style Settings Modal Window */}
      <div className="relative w-full max-w-6xl h-[88vh] max-h-[880px] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col md:flex-row transition-all">
        
        {/* ========================================================= */}
        {/* LEFT VERTICAL SIDEBAR (OBSIDIAN STYLE) */}
        {/* ========================================================= */}
        <div className="w-full md:w-72 bg-slate-50/90 dark:bg-slate-950/80 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 flex flex-col shrink-0">
          
          {/* Sidebar Header */}
          <div className="p-4 sm:p-5 border-b border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-accent/15 text-accent flex items-center justify-center font-bold">
                <Settings className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-sm font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
                  Settings
                </h2>
                <div className="text-[10px] font-mono text-slate-400">
                  Master Data & Taxonomy
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Search Bar */}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Filter settings..."
                value={sidebarSearch}
                onChange={(e) => setSidebarSearch(e.target.value)}
                className="w-full bg-slate-200/60 dark:bg-slate-800/80 border border-slate-300/60 dark:border-slate-700/60 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all"
              />
            </div>
          </div>

          {/* Sidebar Nav List */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
            {filteredSections.map((section) => (
              <div key={section.label} className="space-y-1">
                <div className="px-3 text-[10px] font-mono font-bold tracking-wider text-slate-400 dark:text-slate-500 uppercase">
                  {section.label}
                </div>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id);
                          setIsFormOpen(false);
                          setItemSearch('');
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all group ${
                          isActive
                            ? 'bg-accent/15 dark:bg-accent/25 text-accent dark:text-accent-light shadow-xs'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          <Icon className={`w-4 h-4 shrink-0 transition-colors ${isActive ? 'text-accent dark:text-accent-light' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'}`} />
                          <span className="truncate">{item.label}</span>
                        </div>
                        {item.badge !== null && (
                          <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold ${
                            isActive
                              ? 'bg-accent text-white'
                              : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar Bottom Footer Info */}
          <div className="p-3 border-t border-slate-200/80 dark:border-slate-800/80 text-[10px] font-mono text-slate-400 dark:text-slate-500 flex items-center justify-between">
            <span>DPDP Baseline 2026</span>
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
              <ShieldCheck className="w-3 h-3" />
              <span>Local IDB</span>
            </span>
          </div>

        </div>

        {/* ========================================================= */}
        {/* RIGHT CONTENT PANEL (OBSIDIAN STYLE) */}
        {/* ========================================================= */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-slate-900">
          
          {/* Main Top Header Bar */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  {activeTab === 'overview' && 'Overview & Architecture Baseline'}
                  {activeTab === 'entity' && 'Legal Entity Profile & Assessment Context'}
                  {activeTab === 'exceptions' && 'Statutory Exceptions & Section 17 Safe Harbours'}
                  {activeTab === 'users' && 'Compliance Personnel & Role Assignments'}
                  {activeTab === 'systems' && (mappingSystem ? `Map Obligations: ${mappingSystem.name}` : 'Enterprise Architecture Systems & Mappings')}
                  {activeTab === 'actors' && 'Statutory Actors & Roles'}
                  {activeTab === 'domains' && 'Compliance Lifecycle Domains'}
                  {activeTab === 'tranches' && 'Commencement Tranches & Timelines'}
                  {activeTab === 'owners' && 'Control Owners & Responsible Teams'}
                  {activeTab === 'controls' && 'Operational Control Matrix (OBL-01..54+)'}
                  {activeTab === 'provisions' && 'Statutory Provisions & Rules (Act 2023 / Rules 2025)'}
                  {activeTab === 'reset' && 'Factory Defaults & Reset'}
                </h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {activeTab === 'overview' && 'Summary of compliance master data registered in local browser storage.'}
                {activeTab === 'entity' && 'Configure legal corporate identity, DPO contacts, SDF classification, children data, and cross-border safeguards.'}
                {activeTab === 'exceptions' && 'Record and manage Section 17 statutory safe harbours, foreign outsourcing relief, and adjust obligation applicability.'}
                {activeTab === 'users' && 'Manage compliance officers, DPOs, CISOs, legal counsel, and map them to governance roles.'}
                {activeTab === 'systems' && (mappingSystem ? 'Select which operational controls and statutory rules apply to this system.' : 'Manage custom applications, databases, microservices, and their mapped compliance obligations.')}
                {activeTab === 'actors' && 'Manage statutory actor classifications, legal references, and penalty limits.'}
                {activeTab === 'domains' && 'Manage compliance domain categories across the data lifecycle.'}
                {activeTab === 'tranches' && 'Configure statutory enforcement phases and effective commencement dates.'}
                {activeTab === 'owners' && 'Preset accountable organizational roles and cross-functional teams.'}
                {activeTab === 'controls' && 'Add or update operational controls, descriptions, test IDs, and scopes.'}
                {activeTab === 'provisions' && 'Add or customize legal sections and rules from Gazette notifications.'}
                {activeTab === 'reset' && 'Restore canonical 15 September 2026 Gazette baseline.'}
              </p>
            </div>

            {/* Actions & Close Button */}
            <div className="flex items-center gap-2">
              {saveMessage && (
                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1 rounded-xl border border-emerald-300 dark:border-emerald-800 animate-fade-in">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{saveMessage}</span>
                </span>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title="Close Settings (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Scrollable Content */}
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            
            {/* ------------------------------------------------------------- */}
            {/* 1. OVERVIEW & STATS */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                
                {/* Metric Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div
                    onClick={() => setActiveTab('entity')}
                    className="p-3.5 rounded-2xl bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 hover:border-blue-400 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-blue-600 dark:text-blue-400 uppercase font-bold">Legal Entity</div>
                    <div className="text-sm font-extrabold text-blue-900 dark:text-blue-200 mt-1 truncate">
                      {workspace?.entity_name || 'Enterprise'}
                    </div>
                    <div className="text-[9px] text-blue-500/80 flex items-center justify-between mt-1">
                      <span>{workspace?.is_sdf ? 'SDF Designated' : 'Standard Fiduciary'}</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('exceptions')}
                    className="p-3.5 rounded-2xl bg-amber-50/70 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 hover:border-amber-400 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-amber-600 dark:text-amber-400 uppercase font-bold">Safe Harbours</div>
                    <div className="text-xl font-black text-amber-700 dark:text-amber-300 mt-0.5">
                      {(masterData.exceptions || []).length}
                    </div>
                    <div className="text-[9px] text-amber-500/80 flex items-center justify-between mt-0.5">
                      <span>s.17 Exceptions</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('systems')}
                    className="p-3.5 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-400 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 uppercase font-bold">Systems</div>
                    <div className="text-xl font-black text-indigo-700 dark:text-indigo-300 mt-0.5">
                      {systems.length}
                    </div>
                    <div className="text-[9px] text-indigo-500/80 flex items-center justify-between mt-0.5">
                      <span>Mapped Assets</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('users')}
                    className="p-3.5 rounded-2xl bg-purple-50/70 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 hover:border-purple-400 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-purple-600 dark:text-purple-400 uppercase font-bold">Personnel</div>
                    <div className="text-xl font-black text-purple-700 dark:text-purple-300 mt-0.5">
                      {(masterData.users || []).length}
                    </div>
                    <div className="text-[9px] text-purple-500/80 flex items-center justify-between mt-0.5">
                      <span>Officers</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('controls')}
                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-accent/40 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-slate-500 uppercase">Controls</div>
                    <div className="text-xl font-black text-slate-900 dark:text-slate-100 mt-0.5">
                      {masterData.controls.length}
                    </div>
                    <div className="text-[9px] text-slate-400 flex items-center justify-between mt-0.5">
                      <span>Obligations</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('provisions')}
                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-accent/40 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-slate-500 uppercase">Provisions</div>
                    <div className="text-xl font-black text-sky-600 dark:text-sky-400 mt-0.5">
                      {masterData.provisions.length}
                    </div>
                    <div className="text-[9px] text-slate-400 flex items-center justify-between mt-0.5">
                      <span>Act & Rules</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>

                  <div
                    onClick={() => setActiveTab('domains')}
                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:border-accent/40 cursor-pointer transition-all"
                  >
                    <div className="text-[10px] font-mono text-slate-500 uppercase">Domains</div>
                    <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
                      {masterData.domains.length}
                    </div>
                    <div className="text-[9px] text-slate-400 flex items-center justify-between mt-0.5">
                      <span>Taxonomies</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>

                {/* Obsidian Setting Row: Architecture Info */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-slate-100">
                    <BookOpen className="w-4 h-4 text-accent" />
                    <span>Dynamic Master Data & Regulatory Adaptability</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    This offline register is built to adapt as the Data Protection Board of India (DPB) or MeitY issues new Gazette notifications, clarifications, or amendments. You can add new controls (`OBL-55+`) or adjust statutory provisions without modifying the application code.
                  </p>
                </div>

                {/* Obsidian Setting Row: Quick Actions */}
                <div className="space-y-2">
                  <h4 className="text-xs font-mono font-bold uppercase text-slate-400">Quick Configuration Shortcuts</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={() => setActiveTab('systems')}
                      className="p-3.5 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-400 text-left flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="text-xs font-bold text-indigo-900 dark:text-indigo-200">Custom Systems & Mapping</div>
                        <div className="text-[11px] text-indigo-600/80 dark:text-indigo-400">Map obligations to enterprise apps</div>
                      </div>
                      <Cpu className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </button>

                    <button
                      onClick={() => setActiveTab('controls')}
                      className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-slate-400 text-left flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="text-xs font-bold text-slate-800 dark:text-slate-200">Add Operational Control</div>
                        <div className="text-[11px] text-slate-500">Create new enterprise control obligation</div>
                      </div>
                      <Plus className="w-4 h-4 text-accent" />
                    </button>

                    <button
                      onClick={() => setActiveTab('provisions')}
                      className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-slate-400 text-left flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="text-xs font-bold text-slate-800 dark:text-slate-200">Add Statutory Rule</div>
                        <div className="text-[11px] text-slate-500">Insert new Gazette provision or rule</div>
                      </div>
                      <Plus className="w-4 h-4 text-accent" />
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* LEGAL ENTITY PROFILE & REGULATORY SCOPE TAB */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'entity' && (
              <form onSubmit={handleSaveEntityProfile} className="space-y-6">
                
                {/* Statutory Scope Automation Hero Action Banner */}
                <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-amber-500/10 border border-indigo-200 dark:border-indigo-800/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xs">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-indigo-500/20 mt-0.5">
                      <Zap className="w-5 h-5 fill-current" />
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Automated Statutory Scope Rules Engine
                        </h4>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                          54 Controls Analysis
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        Automatically evaluate and adjust applicability for all 54 DPDP obligations based on this company profile (SDF s.10, Children s.9, Cross-Border s.16, Consent Managers s.6(9), and Safe Harbours s.17).
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenScopeEvaluation}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-extrabold text-xs text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-all shadow-md shadow-indigo-600/30 shrink-0 cursor-pointer w-full md:w-auto"
                  >
                    <Zap className="w-4 h-4 fill-current" />
                    <span>Auto-Apply Scope Rules</span>
                  </button>
                </div>

                {/* Section A: Corporate Registration & Legal Identity */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-700/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Corporate Registration & Legal Identity
                        </h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Official corporate identity of the Data Fiduciary under the Companies Act / Statutory Charters.
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">
                      Section 2(i) Fiduciary
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1 sm:col-span-2">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Legal Entity Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={entityNameInput}
                        onChange={(e) => setEntityNameInput(e.target.value)}
                        placeholder="e.g. Acme FinTech Technologies Private Limited"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Trade / Brand / Operating Name
                      </label>
                      <input
                        type="text"
                        value={tradeBrandNameInput}
                        onChange={(e) => setTradeBrandNameInput(e.target.value)}
                        placeholder="e.g. AcmePay & AcmeWealth"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Corporate Entity Type
                      </label>
                      <select
                        value={entityTypeInput}
                        onChange={(e) => setEntityTypeInput(e.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      >
                        <option value="Private Limited Company">Private Limited Company</option>
                        <option value="Public Limited Company">Public Limited Company</option>
                        <option value="Government Entity / Ministry / Dept">Government Entity / Ministry / Dept</option>
                        <option value="Statutory Corporation / Body">Statutory Corporation / Body</option>
                        <option value="Partnership / LLP">Partnership / LLP</option>
                        <option value="Society / Trust / Section 8 Co">Society / Trust / Section 8 Co</option>
                        <option value="Foreign Enterprise / Branch Office">Foreign Enterprise / Branch Office</option>
                        <option value="Sole Proprietorship">Sole Proprietorship</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        CIN / LLPIN / Registration No.
                      </label>
                      <input
                        type="text"
                        value={cinInput}
                        onChange={(e) => setCinInput(e.target.value)}
                        placeholder="e.g. U72200MH2023PTC123456"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Date of Incorporation
                      </label>
                      <input
                        type="date"
                        value={incorporationDateInput}
                        onChange={(e) => setIncorporationDateInput(e.target.value)}
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Corporate PAN / Tax ID
                      </label>
                      <input
                        type="text"
                        value={panTanInput}
                        onChange={(e) => setPanTanInput(e.target.value)}
                        placeholder="e.g. AAACA1234A"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        GSTIN (Goods & Services Tax)
                      </label>
                      <input
                        type="text"
                        value={gstinInput}
                        onChange={(e) => setGstinInput(e.target.value)}
                        placeholder="e.g. 27AAACA1234A1Z5"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Primary Industry Sector
                      </label>
                      <select
                        value={industryInput}
                        onChange={(e) => setIndustryInput(e.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      >
                        <option value="Fintech & Financial Services (BFSI)">Fintech & Financial Services (BFSI)</option>
                        <option value="Healthcare, Hospitals & MedTech">Healthcare, Hospitals & MedTech</option>
                        <option value="E-Commerce, Retail & Marketplace">E-Commerce, Retail & Marketplace</option>
                        <option value="Telecommunications & Network Service Provider">Telecommunications & Network Service Provider</option>
                        <option value="SaaS, Cloud Software & Enterprise Tech">SaaS, Cloud Software & Enterprise Tech</option>
                        <option value="EdTech & Online Learning Platforms">EdTech & Online Learning Platforms</option>
                        <option value="Travel, Hospitality & Mobility">Travel, Hospitality & Mobility</option>
                        <option value="Media, Streaming, Gaming & Entertainment">Media, Streaming, Gaming & Entertainment</option>
                        <option value="Manufacturing, Supply Chain & Logistics">Manufacturing, Supply Chain & Logistics</option>
                        <option value="Government / Public Utility / Smart City">Government / Public Utility / Smart City</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Corporate Website URL
                      </label>
                      <input
                        type="url"
                        value={websiteInput}
                        onChange={(e) => setWebsiteInput(e.target.value)}
                        placeholder="https://acmepay.in"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Official Legal / Corporate Email
                      </label>
                      <input
                        type="email"
                        value={corporateEmailInput}
                        onChange={(e) => setCorporateEmailInput(e.target.value)}
                        placeholder="compliance@acmepay.in"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Corporate Contact Telephone
                      </label>
                      <input
                        type="tel"
                        value={corporatePhoneInput}
                        onChange={(e) => setCorporatePhoneInput(e.target.value)}
                        placeholder="+91 22 6123 4567"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-3">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Registered Corporate Office Address
                      </label>
                      <textarea
                        rows={2}
                        value={addressInput}
                        onChange={(e) => setAddressInput(e.target.value)}
                        placeholder="e.g. Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-3">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Principal Place of Business / Operating Headquarters (if different)
                      </label>
                      <textarea
                        rows={2}
                        value={headquartersAddressInput}
                        onChange={(e) => setHeadquartersAddressInput(e.target.value)}
                        placeholder="e.g. Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                </div>

                {/* Section B: Statutory Governance Officers */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-700/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold">
                        <UserCheck className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Statutory Governance Officers & Signatories (DPDP s.10, s.8(10))
                        </h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Nominated statutory liaisons for regulatory communication with Data Protection Board, Data Principals, and executive sign-off.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* DPO Section */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-xs text-slate-900 dark:text-slate-100">
                          <ShieldCheck className="w-4 h-4 text-purple-500" />
                          <span>Data Protection Officer (s.10(2)(a))</span>
                        </div>
                        {masterData.users && masterData.users.length > 0 && (
                          <select
                            onChange={(e) => {
                              const found = masterData.users.find(u => u.id === e.target.value);
                              if (found) {
                                setDpoNameInput(`${found.first_name} ${found.last_name}`);
                                setDpoDesignationInput(found.designation);
                                setDpoEmailInput(found.email);
                                setDpoPhoneInput(found.phone);
                              }
                            }}
                            className="text-[10px] px-2 py-0.5 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-semibold"
                            defaultValue=""
                          >
                            <option value="" disabled>⚡ Pick</option>
                            {masterData.users.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="space-y-2 text-xs">
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">DPO Full Name</label>
                          <input
                            type="text"
                            value={dpoNameInput}
                            onChange={(e) => setDpoNameInput(e.target.value)}
                            placeholder="e.g. Adv. Rajesh Sharma"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Designation / Role</label>
                          <input
                            type="text"
                            value={dpoDesignationInput}
                            onChange={(e) => setDpoDesignationInput(e.target.value)}
                            placeholder="e.g. Data Protection Officer & Privacy Director"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Official Email</label>
                          <input
                            type="email"
                            value={dpoEmailInput}
                            onChange={(e) => setDpoEmailInput(e.target.value)}
                            placeholder="dpo@company.com"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Contact Phone</label>
                          <input
                            type="tel"
                            value={dpoPhoneInput}
                            onChange={(e) => setDpoPhoneInput(e.target.value)}
                            placeholder="+91 98765 43210"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Office Location</label>
                          <input
                            type="text"
                            value={dpoOfficeAddressInput}
                            onChange={(e) => setDpoOfficeAddressInput(e.target.value)}
                            placeholder="BKC Mumbai Office, India"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-[11px]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Grievance Redressal Officer Section */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-xs text-slate-900 dark:text-slate-100">
                          <Users className="w-4 h-4 text-emerald-500" />
                          <span>Grievance Officer (s.8(10))</span>
                        </div>
                        {masterData.users && masterData.users.length > 0 && (
                          <select
                            onChange={(e) => {
                              const found = masterData.users.find(u => u.id === e.target.value);
                              if (found) {
                                setGrievanceOfficerNameInput(`${found.first_name} ${found.last_name}`);
                                setGrievanceOfficerDesignationInput(found.designation);
                                setGrievanceOfficerEmailInput(found.email);
                                setGrievanceOfficerPhoneInput(found.phone);
                              }
                            }}
                            className="text-[10px] px-2 py-0.5 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-semibold"
                            defaultValue=""
                          >
                            <option value="" disabled>⚡ Pick</option>
                            {masterData.users.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="space-y-2 text-xs">
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Officer Full Name</label>
                          <input
                            type="text"
                            value={grievanceOfficerNameInput}
                            onChange={(e) => setGrievanceOfficerNameInput(e.target.value)}
                            placeholder="e.g. Priya Iyer"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Designation / Role</label>
                          <input
                            type="text"
                            value={grievanceOfficerDesignationInput}
                            onChange={(e) => setGrievanceOfficerDesignationInput(e.target.value)}
                            placeholder="e.g. Head of Grievance Redressal"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Grievance Email</label>
                          <input
                            type="email"
                            value={grievanceOfficerEmailInput}
                            onChange={(e) => setGrievanceOfficerEmailInput(e.target.value)}
                            placeholder="grievance@company.com"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Grievance Helpline</label>
                          <input
                            type="tel"
                            value={grievanceOfficerPhoneInput}
                            onChange={(e) => setGrievanceOfficerPhoneInput(e.target.value)}
                            placeholder="1800-123-4567"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Redressal Portal URL</label>
                          <input
                            type="url"
                            value={grievancePortalUrlInput}
                            onChange={(e) => setGrievancePortalUrlInput(e.target.value)}
                            placeholder="https://acmepay.in/privacy-grievance"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-[11px]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Authorized Signatory Section */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 font-bold text-xs text-slate-900 dark:text-slate-100">
                          <Award className="w-4 h-4 text-blue-500" />
                          <span>Authorized Signatory / Executive</span>
                        </div>
                        {masterData.users && masterData.users.length > 0 && (
                          <select
                            onChange={(e) => {
                              const found = masterData.users.find(u => u.id === e.target.value);
                              if (found) {
                                setAuthSignatoryNameInput(`${found.first_name} ${found.last_name}`);
                                setAuthSignatoryDesignationInput(found.designation);
                                setAuthSignatoryEmailInput(found.email);
                              }
                            }}
                            className="text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-semibold"
                            defaultValue=""
                          >
                            <option value="" disabled>⚡ Pick</option>
                            {masterData.users.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="space-y-2 text-xs">
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Executive Full Name</label>
                          <input
                            type="text"
                            value={authSignatoryNameInput}
                            onChange={(e) => setAuthSignatoryNameInput(e.target.value)}
                            placeholder="e.g. Vikramaditya Singhania"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Designation / Office</label>
                          <input
                            type="text"
                            value={authSignatoryDesignationInput}
                            onChange={(e) => setAuthSignatoryDesignationInput(e.target.value)}
                            placeholder="e.g. Managing Director & CEO"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono text-slate-500 uppercase">Official Email</label>
                          <input
                            type="email"
                            value={authSignatoryEmailInput}
                            onChange={(e) => setAuthSignatoryEmailInput(e.target.value)}
                            placeholder="ceo@company.com"
                            className="w-full mt-0.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-[11px]"
                          />
                        </div>
                        <div className="text-[10px] text-slate-400 italic pt-2">
                          Signatory for statutory compliance reports, board resolutions, and DPDP audit dossiers.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section C: Statutory Scope & Applicability Criteria */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-700/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                        <Sliders className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Statutory Applicability Criteria & Special Classifications
                        </h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Configure triggers for enhanced statutory obligations under Section 10 (SDF), Section 9 (Children), and Section 16 (Cross-Border).
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    
                    {/* Item 1: Significant Data Fiduciary (SDF) s.10 */}
                    <div className={`p-4 rounded-xl border transition-all ${
                      isSdfInput
                        ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-700/60'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100">
                              Significant Data Fiduciary (SDF) Classification
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                              Section 10
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            Is your organization designated by Central Government (MeitY) as an SDF based on volume, scale, or sensitive profiling?
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={isSdfInput}
                            onChange={(e) => setIsSdfInput(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                        </label>
                      </div>

                      {isSdfInput && (
                        <div className="mt-4 pt-3 border-t border-amber-200 dark:border-amber-800/60 space-y-3 animate-fade-in">
                          <div className="text-[11px] font-bold text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>SDF Enhanced Governance Enforced (OBL-23, OBL-24, OBL-25, OBL-26)</span>
                          </div>
                          <div className="text-[11px] text-slate-600 dark:text-slate-300">
                            Select the statutory assessment factors applicable to your organization under Section 10(1):
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            {[
                              'High volume and velocity of personal data processed',
                              'Risk of harm to Data Principals / sensitive profiling',
                              'Potential impact on sovereignty and integrity of India',
                              'Risk to electoral democracy or public order',
                              'Processing systemic scale children\'s personal data',
                              'State security and national interest implications'
                            ].map((factor) => {
                              const isSelected = sdfFactorsInput.includes(factor);
                              return (
                                <button
                                  type="button"
                                  key={factor}
                                  onClick={() => handleToggleSdfFactor(factor)}
                                  className={`px-3 py-2 rounded-xl text-left font-medium text-[11px] border transition-colors flex items-center gap-2 ${
                                    isSelected
                                      ? 'bg-amber-100/70 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-700'
                                      : 'bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                  }`}
                                >
                                  {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-amber-600 shrink-0" /> : <Square className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                                  <span>{factor}</span>
                                </button>
                              );
                            })}
                          </div>
                          <div>
                            <label className="text-[10px] font-mono text-slate-500 uppercase">Official Gazette Designation Reference (if applicable)</label>
                            <input
                              type="text"
                              value={sdfGazetteInput}
                              onChange={(e) => setSdfGazetteInput(e.target.value)}
                              placeholder="e.g. MeitY Gazette Notification S.O. 412(E) / 2026"
                              className="w-full mt-1 text-xs px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Item 2: Children's Personal Data Processing s.9 */}
                    <div className={`p-4 rounded-xl border transition-all ${
                      processesChildrenInput
                        ? 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-700/60'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100">
                              Children's Personal Data Processing
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
                              Section 9
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            Does your organization process personal data of individuals under 18 years of age?
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={processesChildrenInput}
                            onChange={(e) => setProcessesChildrenInput(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
                        </label>
                      </div>

                      {processesChildrenInput && (
                        <div className="mt-4 pt-3 border-t border-rose-200 dark:border-rose-800/60 space-y-3 animate-fade-in">
                          <div className="text-[11px] font-bold text-rose-900 dark:text-rose-300 flex items-center gap-1.5">
                            <ShieldAlert className="w-3.5 h-3.5" />
                            <span>Strict Prohibition: No Behavioral Tracking or Targeted Advertising (s.9(2), s.9(3))</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                Verifiable Parental Consent Mechanism (s.9(1))
                              </label>
                              <input
                                type="text"
                                value={parentalMechanismInput}
                                onChange={(e) => setParentalMechanismInput(e.target.value)}
                                placeholder="e.g. DigiLocker Parent Token / Aadhaar e-Sign"
                                className="w-full mt-1 text-xs px-3 py-2 rounded-xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              />
                            </div>
                            <div>
                              <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                Age Verification & Gating Protocol
                              </label>
                              <input
                                type="text"
                                value={ageGatingMechanismInput}
                                onChange={(e) => setAgeGatingMechanismInput(e.target.value)}
                                placeholder="e.g. Credit Card Tokenization / Government Token"
                                className="w-full mt-1 text-xs px-3 py-2 rounded-xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Item 3: Data of Persons with Disabilities s.9(1) */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100">
                              Data of Persons with Disabilities
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                              Section 9(1)
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            Does your organization process data belonging to persons with disabilities requiring lawful guardianship consent?
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={processesDisabilitiesInput}
                            onChange={(e) => setProcessesDisabilitiesInput(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                        </label>
                      </div>

                      {processesDisabilitiesInput && (
                        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2 animate-fade-in">
                          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                            Lawful Guardian Consent Verification Protocol
                          </label>
                          <input
                            type="text"
                            value={guardianMechanismInput}
                            onChange={(e) => setGuardianMechanismInput(e.target.value)}
                            placeholder="e.g. Legal Power of Attorney / Court Guardianship Order / Disability ID Token"
                            className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                      )}
                    </div>

                    {/* Item 4: Cross-Border Transfers s.16 */}
                    <div className={`p-4 rounded-xl border transition-all ${
                      hasCrossBorderInput
                        ? 'bg-sky-50/50 dark:bg-sky-950/20 border-sky-300 dark:border-sky-700/60'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100">
                              Cross-Border Personal Data Transfers
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300 border border-sky-300 dark:border-sky-800">
                              Section 16
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            Are digital personal data records transferred or stored outside India (Cloud regions, SaaS, overseas processors)?
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={hasCrossBorderInput}
                            onChange={(e) => setHasCrossBorderInput(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>

                      {hasCrossBorderInput && (
                        <div className="mt-4 pt-3 border-t border-sky-200 dark:border-sky-800/60 space-y-3 animate-fade-in">
                          <div>
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Destination Countries & Jurisdictions
                            </label>
                            <input
                              type="text"
                              value={crossBorderCountriesInput}
                              onChange={(e) => setCrossBorderCountriesInput(e.target.value)}
                              placeholder="e.g. Singapore (AWS AP-Southeast-1), United States (GSuite, Slack), Ireland"
                              className="w-full mt-1 text-xs px-3 py-2 rounded-xl border border-sky-200 dark:border-sky-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Negative List Screening & Transfer Safeguards (s.16)
                            </label>
                            <input
                              type="text"
                              value={crossBorderSafeguardsInput}
                              onChange={(e) => setCrossBorderSafeguardsInput(e.target.value)}
                              placeholder="e.g. MeitY Negative List Country Screening, Standard Contractual Clauses (SCC), Encrypted Transit"
                              className="w-full mt-1 text-xs px-3 py-2 rounded-xl border border-sky-200 dark:border-sky-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Item 5: Consent Managers s.6(9) */}
                    <div className={`p-4 rounded-xl border transition-all ${
                      usesConsentManagersInput
                        ? 'bg-purple-50/50 dark:bg-purple-950/20 border-purple-300 dark:border-purple-700/60'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-900 dark:text-slate-100">
                              Interoperable Consent Managers Integration
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-800">
                              Section 6(9) & Rule 4
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300">
                            Does your application support registered Consent Managers for consent give/withdraw workflows?
                          </p>
                        </div>

                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={usesConsentManagersInput}
                            onChange={(e) => setUsesConsentManagersInput(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>

                      {usesConsentManagersInput && (
                        <div className="mt-4 pt-3 border-t border-purple-200 dark:border-purple-800/60 space-y-2 animate-fade-in">
                          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                            Registered Consent Manager Providers / Gateways
                          </label>
                          <input
                            type="text"
                            value={consentManagerProvidersInput}
                            onChange={(e) => setConsentManagerProvidersInput(e.target.value)}
                            placeholder="e.g. Setu Consent Manager (MeitY Registered), OneMoney AA, Finvu"
                            className="w-full text-xs px-3 py-2 rounded-xl border border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                      )}
                    </div>

                  </div>
                </div>

                {/* Section D: Enterprise Processing Scale & Data Principal Demographics */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-200/80 dark:border-slate-700/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                        <Users className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Enterprise Processing Scale & Data Principal Demographics
                        </h4>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Data volume indicators and principal categories in scope for statutory audit dossiers.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1 sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Estimated Active Data Principals
                      </label>
                      <input
                        type="text"
                        value={estimatedPrincipalsInput}
                        onChange={(e) => setEstimatedPrincipalsInput(e.target.value)}
                        placeholder="e.g. 2,500,000+ active users"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Principal Categories (comma separated)
                      </label>
                      <input
                        type="text"
                        value={principalCategoriesInput}
                        onChange={(e) => setPrincipalCategoriesInput(e.target.value)}
                        placeholder="Retail Customers, Employees, Merchants"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-1">
                      <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        Third-Party Processors in Scope
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={processorsCountInput}
                        onChange={(e) => setProcessorsCountInput(parseInt(e.target.value) || 0)}
                        placeholder="e.g. 12"
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={handleOpenScopeEvaluation}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors shadow-xs cursor-pointer"
                  >
                    <Zap className="w-4 h-4 fill-current text-indigo-500" />
                    <span>⚡ Auto-Apply Scope Rules to Obligations</span>
                  </button>

                  <button
                    type="submit"
                    className="flex items-center justify-center gap-2 px-6 py-2.5 text-xs font-bold text-white bg-accent rounded-xl hover:bg-accent-dark transition-colors shadow-md cursor-pointer"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Company Entity & Scope Settings</span>
                  </button>
                </div>

              </form>
            )}

            {/* ------------------------------------------------------------- */}
            {/* STATUTORY EXCEPTIONS & SECTION 17 SAFE HARBOURS TAB */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'exceptions' && (
              <div className="space-y-4">
                
                {/* Statutory Safe Harbours & Section 17 Applicability Hero Action Banner */}
                <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-amber-500/10 via-rose-500/10 to-indigo-500/10 border border-amber-200 dark:border-amber-800/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xs">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-2xl bg-amber-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-amber-500/20 mt-0.5">
                      <ShieldAlert className="w-5 h-5 fill-current" />
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                          Statutory Safe Harbours & Section 17 Rules Engine
                        </h4>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                          {(masterData.exceptions || []).filter(e => e.status === 'ACTIVE').length} Active Safe Harbours
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        Automatically evaluate and adjust mapped obligations to <strong className="font-semibold text-rose-600 dark:text-rose-400">NOT APPLICABLE</strong> with legal justification, statutory citations (s.17 & s.3(c)), approver metadata, and audit log entries.
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenExceptionsEvaluation()}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-extrabold text-xs text-white bg-amber-600 hover:bg-amber-700 active:scale-95 transition-all shadow-md shadow-amber-600/30 shrink-0 cursor-pointer w-full md:w-auto"
                  >
                    <Zap className="w-4 h-4 fill-current" />
                    <span>Auto-Apply Active Safe Harbours</span>
                  </button>
                </div>

                {/* Top Action & Filter Toolbar */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search exceptions by title, ground (s.17), justification, approver..."
                        value={excSearch}
                        onChange={(e) => setExcSearch(e.target.value)}
                        className="w-full text-xs pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={excCategoryFilter}
                        onChange={(e) => setExcCategoryFilter(e.target.value)}
                        className="text-xs px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
                      >
                        <option value="all">All Safe Harbour Categories</option>
                        <option value="legal_claims">Legal Claims (s.17(1)(a))</option>
                        <option value="fraud_offence_prevention">Fraud & Investigation (s.17(1)(b))</option>
                        <option value="court_order_merger">Court Orders & Mergers (s.17(1)(c))</option>
                        <option value="debt_recovery">Debt Recovery (s.17(1)(e))</option>
                        <option value="research_statistical">Research & Statistics (s.17(2)(b))</option>
                        <option value="foreign_outsourcing_bpo">Foreign Outsourcing BPO (s.3(c))</option>
                        <option value="state_instrumentality">State Instrumentality (s.17(2)(a))</option>
                      </select>

                      <select
                        value={excStatusFilter}
                        onChange={(e) => setExcStatusFilter(e.target.value)}
                        className="text-xs px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
                      >
                        <option value="all">All Statuses</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="PROVISIONAL">PROVISIONAL</option>
                        <option value="UNDER_REVIEW">UNDER_REVIEW</option>
                        <option value="EXPIRED">EXPIRED</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenExceptionsEvaluation()}
                      className="flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-bold text-amber-800 dark:text-amber-200 bg-amber-100 hover:bg-amber-200 dark:bg-amber-950/80 dark:hover:bg-amber-900/80 border border-amber-300 dark:border-amber-700 rounded-xl transition-colors shadow-xs shrink-0 cursor-pointer"
                    >
                      <Zap className="w-3.5 h-3.5 fill-current text-amber-600 dark:text-amber-400" />
                      <span>Auto-Apply ({filteredExceptions.filter(e => e.status === 'ACTIVE').length})</span>
                    </button>

                    <button
                      onClick={handleStartCreateException}
                      className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-amber-600 rounded-xl hover:bg-amber-700 transition-colors shadow-xs shrink-0 cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Record Exception</span>
                    </button>
                  </div>
                </div>

                {/* Statutory Safe Harbour Informational Callout */}
                <div className="p-4 rounded-2xl bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="space-y-1 text-xs">
                    <span className="font-extrabold text-amber-900 dark:text-amber-200">
                      DPDP Statutory Safe Harbours (Section 17 & Section 3(c))
                    </span>
                    <p className="text-amber-800/90 dark:text-amber-300/90 leading-relaxed text-[11px]">
                      Under Section 17, processing personal data for enforcing legal claims, fraud investigation, court decrees, or statistical archiving grants specific statutory safe harbours from Notice (s.5), Consent (s.6), and Data Principal Rights (Chapter III). Use <strong>"Apply Safe Harbour to Obligations"</strong> to adjust mapped controls to <em>NOT APPLICABLE</em> with legal justification.
                    </p>
                  </div>
                </div>

                {/* Exceptions List */}
                <div className="space-y-3.5">
                  {filteredExceptions.map((ex) => {
                    const mappedCount = (ex.mapped_obligation_ids || []).length;
                    return (
                      <div
                        key={ex.id}
                        className="p-4 sm:p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-amber-400/60 transition-all space-y-3 group"
                      >
                        {/* Card Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 dark:border-slate-700/80 pb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-black bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                              {ex.id}
                            </span>
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                              {ex.statutory_ground}
                            </span>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-slate-100">
                              {ex.title}
                            </h4>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-extrabold uppercase border ${
                              ex.status === 'ACTIVE'
                                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                                : ex.status === 'PROVISIONAL'
                                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30'
                                : ex.status === 'UNDER_REVIEW'
                                ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
                                : 'bg-slate-200 text-slate-600 border-slate-300'
                            }`}>
                              {ex.status}
                            </span>
                          </div>
                        </div>

                        {/* Card Body: Justification & Details */}
                        <div className="space-y-2 text-xs">
                          <div className="text-slate-700 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200/80 dark:border-slate-800">
                            <span className="font-bold text-slate-900 dark:text-slate-100 mr-1">Statutory Justification:</span>
                            {ex.justification}
                          </div>

                          {/* Metadata Row */}
                          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                            <div className="flex items-center gap-3">
                              <span>Approved By: <strong className="text-slate-700 dark:text-slate-300">{ex.approved_by}</strong></span>
                              <span>Date: <span className="font-mono">{ex.approval_date}</span></span>
                              {ex.expiry_date && <span>Expires: <span className="font-mono">{ex.expiry_date}</span></span>}
                            </div>
                            <div className="font-mono text-[10px] bg-slate-200/70 dark:bg-slate-800 px-2 py-0.5 rounded">
                              Category: {ex.category.replace(/_/g, ' ')}
                            </div>
                          </div>

                          {/* Mapped Obligations Pills */}
                          {mappedCount > 0 && (
                            <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                                Mapped Controls ({mappedCount}):
                              </span>
                              {ex.mapped_obligation_ids.map(oblId => (
                                <span
                                  key={oblId}
                                  className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/40"
                                >
                                  {oblId}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Card Actions Footer */}
                        <div className="pt-2 border-t border-slate-200/80 dark:border-slate-700/80 flex flex-wrap items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => handleApplyExceptionToDecisionsClick(ex)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-800 dark:text-amber-200 bg-amber-100/70 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 border border-amber-300 dark:border-amber-700 transition-colors shadow-xs"
                            title="Adjust all mapped obligations to NOT APPLICABLE with this statutory justification"
                          >
                            <Zap className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                            <span>⚡ Apply Safe Harbour to Obligations ({mappedCount})</span>
                          </button>

                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleStartEditException(ex)}
                              className="p-1.5 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                              title="Edit Exception"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteException(ex.id, ex.title)}
                              className="p-1.5 text-rose-500 hover:text-rose-700 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                              title="Delete Exception"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                      </div>
                    );
                  })}

                  {filteredExceptions.length === 0 && (
                    <div className="p-8 text-center rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 text-slate-400 space-y-2">
                      <ShieldAlert className="w-8 h-8 mx-auto opacity-40 text-amber-500" />
                      <div className="text-xs font-bold">No statutory exceptions recorded</div>
                      <p className="text-[11px]">Click "Record Statutory Exception" to document a Section 17 legal safe harbour.</p>
                    </div>
                  )}
                </div>

                {/* Exception Add / Edit Modal Dialog */}
                {isExceptionFormOpen && (
                  <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
                    <div className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden">
                      
                      {/* Modal Header */}
                      <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/50">
                        <div className="space-y-0.5">
                          <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 text-amber-600" />
                            <span>{editingExceptionId ? 'Edit Statutory Exception' : 'Record New Statutory Exception'}</span>
                          </h4>
                          <p className="text-[11px] text-slate-500">
                            Document Section 17 or Section 3(c) legal grounds and map relief to specific compliance obligations.
                          </p>
                        </div>
                        <button
                          onClick={() => setIsExceptionFormOpen(false)}
                          className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Modal Form Body */}
                      <form onSubmit={handleSaveExceptionForm} className="p-6 overflow-y-auto space-y-4 flex-1">
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Exception ID</label>
                            <input
                              type="text"
                              value={excIdInput}
                              onChange={(e) => setExcIdInput(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono"
                              placeholder="EXC-001"
                              required
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                              Statutory Ground <span className="text-rose-500">*</span>
                            </label>
                            <select
                              value={excGroundInput}
                              onChange={(e) => setExcGroundInput(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                            >
                              <option value="Section 17(1)(a)">Section 17(1)(a) — Enforcement of Legal Rights & Claims</option>
                              <option value="Section 17(1)(b)">Section 17(1)(b) — Prevention & Investigation of Offences / Cyber Incidents</option>
                              <option value="Section 17(1)(c)">Section 17(1)(c) — Court Orders, Judgments & Mergers / Demergers</option>
                              <option value="Section 17(1)(e)">Section 17(1)(e) — Ascertainment of Financial Info & Debt Recovery</option>
                              <option value="Section 17(2)(b)">Section 17(2)(b) — Research, Archiving or Statistical Purposes</option>
                              <option value="Section 3(c)">Section 3(c) — Foreign Outsourcing / Non-Resident Data Processing (BPO/ITeS)</option>
                              <option value="Section 17(2)(a)">Section 17(2)(a) — State Instrumentality Notified Processing</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                            Exception Title / Operation Name <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={excTitleInput}
                            onChange={(e) => setExcTitleInput(e.target.value)}
                            className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            placeholder="e.g. Litigation Hold & Dispute Resolution Safe Harbour"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Category</label>
                            <select
                              value={excCategoryInput}
                              onChange={(e) => setExcCategoryInput(e.target.value as ExceptionCategory)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            >
                              <option value="legal_claims">Legal Claims & Court Proceedings</option>
                              <option value="fraud_offence_prevention">Fraud & Offence Investigation</option>
                              <option value="court_order_merger">Court Orders & Mergers</option>
                              <option value="debt_recovery">Debt Recovery & Financial</option>
                              <option value="research_statistical">Research & Statistical Archiving</option>
                              <option value="foreign_outsourcing_bpo">Foreign Outsourcing (BPO/ITeS)</option>
                              <option value="state_instrumentality">State Instrumentality</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Status</label>
                            <select
                              value={excStatusInput}
                              onChange={(e) => setExcStatusInput(e.target.value as any)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-bold"
                            >
                              <option value="ACTIVE">ACTIVE</option>
                              <option value="PROVISIONAL">PROVISIONAL</option>
                              <option value="UNDER_REVIEW">UNDER_REVIEW</option>
                              <option value="EXPIRED">EXPIRED</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                            Statutory Justification & Legal Basis <span className="text-rose-500">*</span>
                          </label>
                          <textarea
                            rows={3}
                            value={excJustificationInput}
                            onChange={(e) => setExcJustificationInput(e.target.value)}
                            className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 leading-relaxed"
                            placeholder="State the legal factual matrix and statutory rationale supporting this exemption..."
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Approved By</label>
                              {masterData.users && masterData.users.length > 0 && (
                                <select
                                  onChange={(e) => {
                                    const u = masterData.users.find(usr => usr.id === e.target.value);
                                    if (u) setExcApprovedByInput(`${u.first_name} ${u.last_name} (${u.designation})`);
                                  }}
                                  className="text-[9px] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                                  defaultValue=""
                                >
                                  <option value="" disabled>Pick user</option>
                                  {masterData.users.map(u => (
                                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <input
                              type="text"
                              value={excApprovedByInput}
                              onChange={(e) => setExcApprovedByInput(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="Lead Privacy Counsel"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Approval Date</label>
                            <input
                              type="date"
                              value={excApprovalDateInput}
                              onChange={(e) => setExcApprovalDateInput(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono"
                            />
                          </div>

                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Expiry Date (Optional)</label>
                            <input
                              type="date"
                              value={excExpiryDateInput}
                              onChange={(e) => setExcExpiryDateInput(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono"
                            />
                          </div>
                        </div>

                        {/* Mapped Control Obligations & Statutory Rules Multi-Select Checklist */}
                        <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <label className="text-xs font-bold text-slate-900 dark:text-slate-100">
                              Mapped Obligations & Statutory Rules ({excMappedObligationsInput.length} Selected)
                            </label>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => {
                                  const ctrlIds = (masterData.controls || []).map(c => c.id);
                                  setExcMappedObligationsInput(prev => Array.from(new Set([...prev, ...ctrlIds])));
                                }}
                                className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold hover:underline cursor-pointer"
                              >
                                + All Controls ({masterData.controls.length})
                              </button>
                              <span className="text-slate-300">•</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const provIds = (masterData.provisions || []).map(p => p.id);
                                  setExcMappedObligationsInput(prev => Array.from(new Set([...prev, ...provIds])));
                                }}
                                className="text-[10px] text-sky-600 dark:text-sky-400 font-semibold hover:underline cursor-pointer"
                              >
                                + All Rules ({masterData.provisions.length})
                              </button>
                              <span className="text-slate-300">•</span>
                              <button
                                type="button"
                                onClick={() => setExcMappedObligationsInput([])}
                                className="text-[10px] text-slate-500 font-semibold hover:underline cursor-pointer"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>

                          {/* Filter Tabs & Search for Exception Targets */}
                          <div className="flex items-center justify-between gap-2 pt-1">
                            <div className="flex items-center gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                              {[
                                { id: 'all', label: `All (${combinedExceptionTargetOptions.length})` },
                                { id: 'control', label: `Controls (${masterData.controls.length})` },
                                { id: 'provision', label: `Rules (${masterData.provisions.length})` },
                              ].map(tab => (
                                <button
                                  key={tab.id}
                                  type="button"
                                  onClick={() => setExcObligationFilterType(tab.id as any)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                    excObligationFilterType === tab.id
                                      ? 'bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 shadow-xs'
                                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-200'
                                  }`}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>

                            <div className="relative flex-1 max-w-[200px]">
                              <Search className="w-3 h-3 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                              <input
                                type="text"
                                placeholder="Search items..."
                                value={excObligationSearch}
                                onChange={(e) => setExcObligationSearch(e.target.value)}
                                className="w-full text-[10px] pl-7 pr-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-amber-500"
                              />
                            </div>
                          </div>

                          <div className="max-h-48 overflow-y-auto p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 space-y-1.5">
                            {filteredExceptionTargetOptions.length === 0 ? (
                              <div className="text-center py-4 text-slate-400 text-xs">
                                No obligations or rules match your search.
                              </div>
                            ) : (
                              filteredExceptionTargetOptions.map((opt) => {
                                const isChecked = excMappedObligationsInput.includes(opt.id);
                                return (
                                  <button
                                    type="button"
                                    key={opt.id}
                                    onClick={() => handleToggleExceptionMappedObligation(opt.id)}
                                    className={`w-full p-2 rounded-lg text-left text-xs flex items-start gap-2.5 transition-colors cursor-pointer ${
                                      isChecked
                                        ? 'bg-amber-100/80 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-800 text-amber-950 dark:text-amber-100 font-semibold'
                                        : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                                    }`}
                                  >
                                    {isChecked ? (
                                      <CheckSquare className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                    ) : (
                                      <Square className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    )}
                                    <div className="truncate flex-1">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-mono font-bold text-[10px] bg-slate-200 dark:bg-slate-800 px-1.5 py-0.2 rounded">
                                          {opt.id}
                                        </span>
                                        <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded uppercase ${
                                          opt.type === 'control'
                                            ? 'bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300'
                                            : 'bg-sky-100 dark:bg-sky-900/60 text-sky-700 dark:text-sky-300'
                                        }`}>
                                          {opt.type === 'control' ? 'Control' : 'Rule'}
                                        </span>
                                        <span className="truncate">{opt.title}</span>
                                      </div>
                                      <div className="text-[10px] text-slate-400 font-normal truncate mt-0.5">
                                        {opt.subtitle}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                            Scope Boundaries & Operational Notes
                          </label>
                          <textarea
                            rows={2}
                            value={excNotesInput}
                            onChange={(e) => setExcNotesInput(e.target.value)}
                            className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                            placeholder="Optional operational notes, e.g. records to be segregated in separate litigation escrow database..."
                          />
                        </div>

                        {/* Modal Footer Buttons */}
                        <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-200 dark:border-slate-800">
                          <button
                            type="button"
                            onClick={() => setIsExceptionFormOpen(false)}
                            className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors shadow-xs"
                          >
                            Save Statutory Exception
                          </button>
                        </div>

                      </form>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 2. CUSTOM ENTERPRISE SYSTEMS & OBLIGATION MAPPING */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'systems' && (
              <div className="space-y-5">
                
                {/* MODE A: OBLIGATION MAPPER SUB-VIEW FOR A SPECIFIC SYSTEM */}
                {mappingSystem ? (
                  <div className="space-y-4 animate-fade-in">
                    
                    {/* Header Banner */}
                    <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <button
                          onClick={() => setMappingSystem(null)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100 transition-colors"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                          <span>Back to All Systems</span>
                        </button>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-sm font-black text-slate-900 dark:text-slate-100">
                            Mapping Obligations: <span className="text-indigo-600 dark:text-indigo-400">{mappingSystem.name}</span>
                          </h4>
                          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20 whitespace-nowrap shrink-0">
                            {mappingSystem.id}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Toggle which Control Obligations and Statutory Provisions are processed by or enforced on this system.
                        </p>
                      </div>

                      {/* Right Action Group */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 shadow-xs">
                          {selectedObligationIds.length} Mapped
                        </span>
                        <button
                          onClick={handleSaveMapper}
                          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-all shadow-xs"
                        >
                          <Check className="w-4 h-4" />
                          <span>Save & Apply</span>
                        </button>
                      </div>
                    </div>

                    {/* Filters & Bulk Actions Bar */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                      <div className="relative flex-1">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search obligations by ID, title, scope..."
                          value={mappingSearch}
                          onChange={(e) => setMappingSearch(e.target.value)}
                          className="w-full text-xs pl-9 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100"
                        />
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0 overflow-x-auto pb-1 sm:pb-0">
                        <button
                          onClick={() => {
                            const filtered = masterData.controls
                              .filter(c => !mappingSearch || c.id.toLowerCase().includes(mappingSearch.toLowerCase()) || (c.title || c.name).toLowerCase().includes(mappingSearch.toLowerCase()) || (c.scope || '').toLowerCase().includes(mappingSearch.toLowerCase()))
                              .map(c => c.id);
                            handleSelectAllFiltered(filtered);
                          }}
                          className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                          Select Filtered
                        </button>
                        <button
                          onClick={handleDeselectAll}
                          className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    {/* Domain Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
                      <button
                        onClick={() => setMappingDomainFilter('all')}
                        className={`px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors shrink-0 ${
                          mappingDomainFilter === 'all'
                            ? 'bg-accent text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                        }`}
                      >
                        All Domains
                      </button>
                      {masterData.domains.map(dom => (
                        <button
                          key={dom.slug}
                          onClick={() => setMappingDomainFilter(dom.slug)}
                          className={`px-2.5 py-1 rounded-lg font-semibold text-[11px] transition-colors shrink-0 ${
                            mappingDomainFilter === dom.slug
                              ? 'bg-accent text-white'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                          }`}
                        >
                          {dom.title}
                        </button>
                      ))}
                    </div>

                    {/* Obligations Checklist Grid */}
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                      {masterData.controls
                        .filter(c => {
                          if (mappingDomainFilter !== 'all' && c.domain_slug !== mappingDomainFilter) return false;
                          if (!mappingSearch) return true;
                          const q = mappingSearch.toLowerCase();
                          return (
                            c.id.toLowerCase().includes(q) ||
                            (c.title || c.name || '').toLowerCase().includes(q) ||
                            (c.scope || '').toLowerCase().includes(q) ||
                            (c.description || '').toLowerCase().includes(q)
                          );
                        })
                        .map(ctrl => {
                          const isSelected = selectedObligationIds.includes(ctrl.id);
                          return (
                            <div
                              key={ctrl.id}
                              onClick={() => handleToggleObligationInMapper(ctrl.id)}
                              className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-start gap-3 select-none ${
                                isSelected
                                  ? 'bg-indigo-50/50 dark:bg-indigo-950/20 border-indigo-300 dark:border-indigo-700/80 shadow-xs'
                                  : 'bg-slate-50/70 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                              }`}
                            >
                              <div className="pt-0.5 shrink-0">
                                {isSelected ? (
                                  <CheckSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                ) : (
                                  <Square className="w-4 h-4 text-slate-400" />
                                )}
                              </div>

                              <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-mono font-bold text-xs ${isSelected ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-300'}`}>
                                    {ctrl.id}
                                  </span>
                                  <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                                    {ctrl.title || ctrl.name}
                                  </span>
                                  <span className="px-2 py-0.2 rounded text-[10px] font-mono text-slate-500 bg-slate-200/60 dark:bg-slate-700/60">
                                    {ctrl.scope}
                                  </span>
                                  <span className="px-2 py-0.2 rounded text-[10px] font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                                    {ctrl.domain_title || ctrl.domain}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                                  {ctrl.description || ctrl.control}
                                </p>
                              </div>

                              <div className="text-right shrink-0">
                                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                                  isSelected
                                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20'
                                    : 'bg-slate-200/60 dark:bg-slate-700/60 text-slate-400'
                                }`}>
                                  {isSelected ? 'Mapped' : 'Unmapped'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Bottom Save Bar */}
                    <div className="pt-2 flex items-center justify-between border-t border-slate-200 dark:border-slate-800">
                      <span className="text-xs text-slate-500">
                        Total {selectedObligationIds.length} obligations will be bound to <strong className="font-mono font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">{mappingSystem.id}</strong>
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setMappingSystem(null)}
                          className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveMapper}
                          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-all shadow-xs"
                        >
                          <Check className="w-4 h-4" />
                          <span>Save System Obligations</span>
                        </button>
                      </div>
                    </div>

                  </div>
                ) : (
                  /* MODE B: ALL SYSTEMS LISTING & MANAGEMENT */
                  <div className="space-y-4">
                    
                    {/* Top Search & Create Bar */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="relative flex-1">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            placeholder="Search systems by ID, name, owner, or data category..."
                            value={systemSearch}
                            onChange={(e) => setSystemSearch(e.target.value)}
                            className="w-full text-xs pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100"
                          />
                        </div>

                        <select
                          value={systemTypeFilter}
                          onChange={(e) => setSystemTypeFilter(e.target.value)}
                          className="text-xs py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        >
                          <option value="all">All System Types</option>
                          {SYSTEM_TYPES.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={handleStartCreateSystem}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shrink-0 shadow-xs"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>New Enterprise System</span>
                      </button>
                    </div>

                    {/* System Form */}
                    {isSystemFormOpen && (
                      <form onSubmit={handleSaveSystemForm} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3 animate-fade-in">
                        <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                          {editingSystemId ? `Edit Enterprise System: ${editingSystemId}` : 'Register New Enterprise System'}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <input
                            type="text"
                            required
                            placeholder="System ID (e.g. SYS-001 / APP-PROD-01)"
                            value={sysIdInput}
                            onChange={(e) => setSysIdInput(e.target.value)}
                            className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-mono text-slate-900 dark:text-slate-100"
                          />
                          <input
                            type="text"
                            required
                            placeholder="System Name (e.g. Customer Core Portal)"
                            value={sysNameInput}
                            onChange={(e) => setSysNameInput(e.target.value)}
                            className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 sm:col-span-2 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <select
                            value={sysTypeInput}
                            onChange={(e) => setSysTypeInput(e.target.value as SystemType)}
                            className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                          >
                            {SYSTEM_TYPES.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="System Owner / Team (e.g. Platform Engineering)"
                            value={sysOwnerInput}
                            onChange={(e) => setSysOwnerInput(e.target.value)}
                            className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="In-Scope Personal Data Categories (comma-separated, e.g. KYC, Biometrics, Logs)"
                          value={sysDataCatInput}
                          onChange={(e) => setSysDataCatInput(e.target.value)}
                          className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                        />
                        <textarea
                          rows={2}
                          placeholder="System description, architecture role, or hosting environment..."
                          value={sysDescInput}
                          onChange={(e) => setSysDescInput(e.target.value)}
                          className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                        />
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setIsSystemFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                          <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save System</button>
                        </div>
                      </form>
                    )}

                    {/* Systems Cards Grid */}
                    <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
                      {systems
                        .filter(s => {
                          if (systemTypeFilter !== 'all' && s.type !== systemTypeFilter) return false;
                          if (!systemSearch) return true;
                          const q = systemSearch.toLowerCase();
                          return (
                            s.id.toLowerCase().includes(q) ||
                            s.name.toLowerCase().includes(q) ||
                            (s.owner || '').toLowerCase().includes(q) ||
                            (s.description || '').toLowerCase().includes(q) ||
                            (s.data_categories || []).some(cat => cat.toLowerCase().includes(q))
                          );
                        })
                        .map(sys => {
                          const mappedObligations = getMappedObligationsForSystem(sys.id);
                          return (
                            <div
                              key={sys.id}
                              className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                            >
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="p-2 rounded-xl bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0">
                                  {getTypeIcon(sys.type)}
                                </div>

                                <div className="space-y-1.5 flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono font-bold text-xs text-indigo-600 dark:text-indigo-400 whitespace-nowrap shrink-0">
                                      {sys.id}
                                    </span>
                                    <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100">
                                      {sys.name}
                                    </h4>
                                    <span className="px-2 py-0.2 rounded text-[10px] font-medium bg-slate-200/60 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300">
                                      {sys.type}
                                    </span>
                                    {sys.owner && (
                                      <span className="text-[10px] text-slate-400">
                                        Owner: <strong className="text-slate-600 dark:text-slate-300">{sys.owner}</strong>
                                      </span>
                                    )}
                                  </div>

                                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                                    {sys.description}
                                  </p>

                                  {/* Data Categories & Mapped Obligations preview chips */}
                                  <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                    {sys.data_categories && sys.data_categories.length > 0 && (
                                      <div className="flex items-center gap-1 text-[10px] text-slate-500 mr-2">
                                        <Tag className="w-2.5 h-2.5" />
                                        <span>{sys.data_categories.join(', ')}</span>
                                      </div>
                                    )}

                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20">
                                      {mappedObligations.length} Obligations Mapped
                                    </span>

                                    {mappedObligations.slice(0, 4).map(oblId => (
                                      <span key={oblId} className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400">
                                        {oblId}
                                      </span>
                                    ))}
                                    {mappedObligations.length > 4 && (
                                      <span className="text-[9px] font-mono text-slate-400">
                                        +{mappedObligations.length - 4} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                                <button
                                  onClick={() => handleOpenMapper(sys)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-xs"
                                  title="Map Control Obligations to this System"
                                >
                                  <Sliders className="w-3.5 h-3.5" />
                                  <span>Map Obligations</span>
                                </button>

                                <button
                                  onClick={() => handleStartEditSystem(sys)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 transition-colors"
                                  title="Edit System"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>

                                <button
                                  onClick={() => handleDeleteSystemClick(sys.id, sys.name)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                                  title="Delete System"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                      {systems.length === 0 && (
                        <div className="p-8 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 text-center space-y-2">
                          <Cpu className="w-8 h-8 text-slate-400 mx-auto" />
                          <div className="text-xs font-bold text-slate-700 dark:text-slate-300">No Enterprise Systems Registered</div>
                          <p className="text-xs text-slate-400">Add your organization's apps, databases, and microservices to map technical DPDP obligations.</p>
                          <button
                            onClick={handleStartCreateSystem}
                            className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-accent"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add First System</span>
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                )}

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 3. STATUTORY ACTORS */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'actors' && (
              <div className="space-y-4">
                
                {/* Header Action Row */}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">
                    Define statutory actors with legal definitions and section 33 penalty ceilings
                  </span>
                  <button
                    onClick={() => {
                      setEditingItem({ id: '', name: '', statutory_ref: 'Section 2(...)', description: '', max_penalty: 'Up to ₹250 Crore' });
                      setIsFormOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Actor Role</span>
                  </button>
                </div>

                {/* Inline Obsidian Edit Form */}
                {isFormOpen && (
                  <form onSubmit={handleSaveActor} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3">
                    <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                      {editingItem?.id ? 'Edit Actor Role' : 'Create New Statutory Actor Role'}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Actor Name (e.g. Joint Data Fiduciary)"
                        value={editingItem?.name || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        placeholder="Statutory Reference (e.g. Section 2(i))"
                        value={editingItem?.statutory_ref || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, statutory_ref: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      rows={2}
                      placeholder="Legal Definition & Statutory Duties..."
                      value={editingItem?.description || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                      className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save Actor</button>
                    </div>
                  </form>
                )}

                {/* Actor Rows List */}
                <div className="space-y-2.5">
                  {masterData.actors.map((actor) => (
                    <div key={actor.id} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex items-start justify-between gap-4 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-xs text-slate-900 dark:text-slate-100">{actor.name}</span>
                          {actor.statutory_ref && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-mono bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                              {actor.statutory_ref}
                            </span>
                          )}
                          {actor.max_penalty && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-mono bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                              {actor.max_penalty}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{actor.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingItem(actor); setIsFormOpen(true); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                          title="Edit Actor"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteActor(actor.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                          title="Delete Actor"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 3. COMPLIANCE DOMAINS */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'domains' && (
              <div className="space-y-4">
                
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">
                    Manage compliance lifecycle taxonomy domains for categorizing obligations
                  </span>
                  <button
                    onClick={() => {
                      setEditingItem({ id: '', slug: '', title: '', description: '' });
                      setIsFormOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Domain</span>
                  </button>
                </div>

                {isFormOpen && (
                  <form onSubmit={handleSaveDomain} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3">
                    <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                      {editingItem?.id ? 'Edit Compliance Domain' : 'Create New Compliance Domain'}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Domain Slug (e.g. ai-governance)"
                        value={editingItem?.slug || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Domain Title (e.g. AI Governance & Automated Decisions)"
                        value={editingItem?.title || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      rows={2}
                      placeholder="Domain Scope Description..."
                      value={editingItem?.description || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                      className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save Domain</button>
                    </div>
                  </form>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {masterData.domains.map((dom) => (
                    <div key={dom.id} className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-slate-900 dark:text-slate-100">{dom.title}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-accent/10 text-accent dark:text-accent-light">
                            {dom.slug}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">{dom.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingItem(dom); setIsFormOpen(true); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteDomain(dom.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 4. COMMENCEMENT TRANCHES */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'tranches' && (
              <div className="space-y-4">
                
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">
                    Manage statutory commencement tranches, deadlines, and status badges
                  </span>
                  <button
                    onClick={() => {
                      setEditingItem({ id: '2028-01-01', name: 'Tranche 4', date: '01 Jan 2028', status: 'Future Tranche', headline: 'Future Rule Commencement', description: '', colorClass: 'indigo' });
                      setIsFormOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Tranche</span>
                  </button>
                </div>

                {isFormOpen && (
                  <form onSubmit={handleSaveTranche} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3">
                    <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                      {editingItem?.name ? `Edit: ${editingItem.name}` : 'Create New Tranche'}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="date"
                        required
                        value={editingItem?.id || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, id: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-mono"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Tranche Name (e.g. Tranche 2)"
                        value={editingItem?.name || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        placeholder="Display Date (e.g. 13 Nov 2026)"
                        value={editingItem?.date || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, date: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Headline / Focus"
                        value={editingItem?.headline || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, headline: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        placeholder="Status (e.g. In Force / Scheduled)"
                        value={editingItem?.status || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      rows={2}
                      placeholder="Tranche scope description..."
                      value={editingItem?.description || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                      className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save Tranche</button>
                    </div>
                  </form>
                )}

                <div className="space-y-3">
                  {masterData.tranches.map((t) => (
                    <div key={t.id} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex items-start justify-between gap-4">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs text-slate-900 dark:text-slate-100">{t.name}</span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-accent/15 text-accent border border-accent/20">
                            {t.date}
                          </span>
                          <span className="text-[10px] font-semibold text-slate-500">({t.status})</span>
                        </div>
                        <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">{t.headline}</div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">{t.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingItem(t); setIsFormOpen(true); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTranche(t.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* PERSONNEL & USERS MANAGEMENT */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                
                {/* Top Action & Filter Toolbar */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search by name, designation, email, phone, role..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="w-full text-xs pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-accent"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={userDeptFilter}
                        onChange={(e) => setUserDeptFilter(e.target.value)}
                        className="text-xs px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent"
                      >
                        <option value="all">All Departments</option>
                        {departmentOptions.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>

                      <select
                        value={userRoleFilter}
                        onChange={(e) => setUserRoleFilter(e.target.value)}
                        className="text-xs px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent max-w-[160px]"
                      >
                        <option value="all">All Mapped Roles</option>
                        {masterData.owners.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={handleStartCreateUser}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-accent rounded-xl hover:bg-accent-dark transition-colors shadow-xs shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Personnel</span>
                  </button>
                </div>

                {/* Users Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {filteredUsers.map((user) => {
                    const initials = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || 'U';
                    return (
                      <div
                        key={user.id}
                        className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-all flex flex-col justify-between group"
                      >
                        <div className="space-y-3">
                          {/* Header with Avatar & Names */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-accent to-indigo-600 text-white font-black text-sm flex items-center justify-center shrink-0 shadow-xs">
                                {initials}
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-extrabold text-sm text-slate-900 dark:text-slate-100">
                                    {user.first_name} {user.last_name}
                                  </span>
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                    {user.id}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-accent dark:text-accent-light mt-0.5">
                                  <Briefcase className="w-3 h-3 shrink-0" />
                                  <span>{user.designation}</span>
                                </div>
                              </div>
                            </div>

                            {/* Card Actions */}
                            <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleStartEditUser(user)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                title="Edit Personnel"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                                title="Delete Personnel"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Department and Contact Details */}
                          <div className="space-y-1.5 pt-1 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                                {user.department || 'General Compliance'}
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5 text-xs text-slate-600 dark:text-slate-400 font-mono">
                              <a
                                href={`mailto:${user.email}`}
                                className="flex items-center gap-1.5 hover:text-accent transition-colors"
                                title="Send Email"
                              >
                                <Mail className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                <span className="underline decoration-dotted underline-offset-2">{user.email}</span>
                              </a>
                              {user.phone && (
                                <a
                                  href={`tel:${user.phone}`}
                                  className="flex items-center gap-1.5 hover:text-emerald-500 transition-colors"
                                  title="Call Phone"
                                >
                                  <Phone className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                  <span>{user.phone}</span>
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Notes if present */}
                          {user.notes && (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900/60 p-2 rounded-xl border border-slate-200/60 dark:border-slate-800 leading-relaxed">
                              {user.notes}
                            </p>
                          )}
                        </div>

                        {/* Mapped Roles Section */}
                        <div className="mt-3 pt-3 border-t border-slate-200/70 dark:border-slate-700/60">
                          <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 flex items-center justify-between">
                            <span>Mapped Control & Statutory Roles</span>
                            <span className="text-slate-400">{(user.mapped_roles || []).length}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(user.mapped_roles || []).map((role) => (
                              <span
                                key={role}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60"
                              >
                                <Check className="w-2.5 h-2.5 stroke-[3]" />
                                <span>{role}</span>
                              </span>
                            ))}
                            {(!user.mapped_roles || user.mapped_roles.length === 0) && (
                              <span className="text-[11px] text-slate-400 italic">No roles mapped</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredUsers.length === 0 && (
                  <div className="p-8 text-center rounded-2xl bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-800 text-slate-500">
                    <Users className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
                    <p className="text-xs font-semibold">No compliance personnel found matching query</p>
                    <button
                      onClick={handleStartCreateUser}
                      className="mt-3 px-3 py-1.5 text-xs font-bold text-accent hover:underline inline-flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Register new personnel</span>
                    </button>
                  </div>
                )}

                {/* Add / Edit Personnel Modal Dialog */}
                {isUserFormOpen && (
                  <div className="fixed inset-0 z-60 overflow-y-auto flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-xs">
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
                      {/* Header */}
                      <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
                        <div>
                          <h4 className="text-base font-bold text-slate-900 dark:text-slate-100">
                            {editingUserId ? `Edit Personnel: ${userFormFirstName} ${userFormLastName}` : 'Add Compliance Personnel'}
                          </h4>
                          <p className="text-xs text-slate-500">
                            Register employee details, designation, contact info, and map them to control owner roles.
                          </p>
                        </div>
                        <button
                          onClick={() => setIsUserFormOpen(false)}
                          className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Form Body */}
                      <form onSubmit={handleSaveUserForm} className="p-6 overflow-y-auto space-y-4 flex-1">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Personnel ID</label>
                            <input
                              type="text"
                              value={userFormId}
                              onChange={(e) => setUserFormId(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono"
                              placeholder="USR-001"
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">First Name</label>
                            <input
                              type="text"
                              value={userFormFirstName}
                              onChange={(e) => setUserFormFirstName(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="e.g. Priya"
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Last Name</label>
                            <input
                              type="text"
                              value={userFormLastName}
                              onChange={(e) => setUserFormLastName(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="e.g. Sharma"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Designation / Title</label>
                            <input
                              type="text"
                              value={userFormDesignation}
                              onChange={(e) => setUserFormDesignation(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="e.g. Data Protection Officer (DPO)"
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Department</label>
                            <input
                              type="text"
                              list="deptList"
                              value={userFormDepartment}
                              onChange={(e) => setUserFormDepartment(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="e.g. Legal & Compliance"
                            />
                            <datalist id="deptList">
                              {departmentOptions.map(d => <option key={d} value={d} />)}
                            </datalist>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Email Address</label>
                            <input
                              type="email"
                              value={userFormEmail}
                              onChange={(e) => setUserFormEmail(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="priya.sharma@acmecorp.in"
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Contact Phone Number</label>
                            <input
                              type="tel"
                              value={userFormPhone}
                              onChange={(e) => setUserFormPhone(e.target.value)}
                              className="w-full mt-1 p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder="+91 98101 23456"
                            />
                          </div>
                        </div>

                        {/* Role Mapping Section */}
                        <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                          <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center justify-between">
                            <span>Mapped Governance & Control Roles</span>
                            <span className="text-[11px] font-normal text-slate-500 font-mono">
                              {userFormMappedRoles.length} selected
                            </span>
                          </label>
                          <p className="text-[11px] text-slate-500">
                            Select which master roles this user is qualified and designated to execute:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 max-h-48 overflow-y-auto p-1">
                            {masterData.owners.map((ownerRole) => {
                              const isChecked = userFormMappedRoles.includes(ownerRole);
                              return (
                                <button
                                  key={ownerRole}
                                  type="button"
                                  onClick={() => handleToggleUserMappedRole(ownerRole)}
                                  className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-left text-xs font-medium transition-all ${
                                    isChecked
                                      ? 'bg-accent/10 dark:bg-accent/20 border-accent text-accent dark:text-accent-light'
                                      : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 ${
                                    isChecked ? 'bg-accent border-accent text-white' : 'border-slate-300 dark:border-slate-600'
                                  }`}>
                                    {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                                  </div>
                                  <span className="truncate">{ownerRole}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Notes / Responsibilities */}
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                            Statutory Mandate / Internal Responsibilities
                          </label>
                          <textarea
                            rows={2}
                            value={userFormNotes}
                            onChange={(e) => setUserFormNotes(e.target.value)}
                            placeholder="e.g. Lead liaison for Data Protection Board of India inquiries; manages DPIA approvals..."
                            className="w-full p-2 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          />
                        </div>

                        {/* Footer Buttons */}
                        <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-200 dark:border-slate-800">
                          <button
                            type="button"
                            onClick={() => setIsUserFormOpen(false)}
                            className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-5 py-2 text-xs font-bold text-white bg-accent hover:bg-accent-dark rounded-xl transition-colors shadow-xs"
                          >
                            Save Personnel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 5. CONTROL OWNERS */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'owners' && (
              <div className="space-y-4">
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Preset accountable roles and teams across departments</span>
                </div>

                {/* Add Owner Obsidian Row */}
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200">Add Control Owner</div>
                    <div className="text-[11px] text-slate-500">Enter a team or role title to appear in assignment dropdowns</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      id="newOwnerInputSidebar"
                      placeholder="e.g. Fraud & AML Compliance"
                      className="text-xs p-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddOwner((e.target as HTMLInputElement).value);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('newOwnerInputSidebar') as HTMLInputElement;
                        handleAddOwner(input.value);
                        input.value = '';
                      }}
                      className="px-4 py-2 text-xs font-bold text-white bg-accent rounded-xl hover:bg-accent-dark transition-colors shrink-0"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Owners Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {masterData.owners.map((owner) => (
                    <div key={owner} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{owner}</span>
                      <button
                        onClick={() => handleDeleteOwner(owner)}
                        className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                        title="Delete Owner"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 6. CONTROL MATRIX */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'controls' && (
              <div className="space-y-4">
                
                <div className="flex items-center justify-between gap-3">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search controls by ID, title, scope..."
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      className="w-full text-xs pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100"
                    />
                  </div>

                  <button
                    onClick={() => {
                      const nextNum = masterData.controls.length + 1;
                      setEditingItem({
                        id: `OBL-${String(nextNum).padStart(2, '0')}`,
                        alias: `OBL-${String(nextNum).padStart(2, '0')}`,
                        title: '',
                        name: '',
                        description: '',
                        scope: 's.8',
                        domain: 'Security Safeguards & Access',
                        domain_slug: 'security',
                        domain_title: 'Technical Safeguards',
                        recommended_owner: 'CISO / Security Operations',
                        systems: 'SYS-007',
                        test_id: `SPEC-OBL-${String(nextNum).padStart(2, '0')}`,
                        expected: '',
                        evidence: 'out/audit/workpaper.json',
                        legal_source_records: [{ commencement: '2027-05-13', actor: 'Data Fiduciary', trigger: 'Processing' }]
                      });
                      setIsFormOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shrink-0 shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>New Control</span>
                  </button>
                </div>

                {isFormOpen && (
                  <form onSubmit={handleSaveControl} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3">
                    <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                      {editingItem?.id ? `Edit Control: ${editingItem.id}` : 'Create New Control Obligation'}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Control ID (e.g. OBL-55)"
                        value={editingItem?.id || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, id: e.target.value, alias: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-mono text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Control Title"
                        value={editingItem?.title || editingItem?.name || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value, name: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 col-span-2 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Statutory Scope (e.g. s.8(5), Rule 7)"
                        value={editingItem?.scope || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, scope: e.target.value, provision: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        placeholder="Recommended Owner"
                        value={editingItem?.recommended_owner || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, recommended_owner: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>

                    {/* Mapped Systems Multi-Select Selector in Control Form */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                          Mapped Target Systems ({systems.length} available)
                        </label>
                        <span className="text-[10px] text-slate-400">
                          Check all systems in-scope for this control
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 max-h-36 overflow-y-auto">
                        {systems.map(s => {
                          const currentSysList = (editingItem?.systems || '').split(';').map((x: string) => x.trim()).filter(Boolean);
                          const isChecked = currentSysList.includes(s.id);
                          return (
                            <label key={s.id} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer select-none hover:text-slate-900 dark:hover:text-white">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const updatedList = e.target.checked
                                    ? [...currentSysList, s.id]
                                    : currentSysList.filter((x: string) => x !== s.id);
                                  setEditingItem({ ...editingItem, systems: updatedList.join('; ') });
                                }}
                                className="rounded text-accent focus:ring-accent"
                              />
                              <span className="font-mono font-bold text-[11px] text-indigo-600 dark:text-indigo-400">{s.id}</span>
                              <span className="truncate text-[10px] text-slate-500">{s.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <textarea
                      rows={2}
                      placeholder="Control description and operational requirements..."
                      value={editingItem?.description || editingItem?.control || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value, control: e.target.value })}
                      className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save Control</button>
                    </div>
                  </form>
                )}

                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {masterData.controls
                    .filter(c => !itemSearch || c.id.toLowerCase().includes(itemSearch.toLowerCase()) || (c.title || c.name).toLowerCase().includes(itemSearch.toLowerCase()) || (c.scope || '').toLowerCase().includes(itemSearch.toLowerCase()) || (c.systems || '').toLowerCase().includes(itemSearch.toLowerCase()))
                    .map((ctrl) => {
                      const docLink = getGovernmentLinkage(ctrl, true);
                      const ctrlSystems = (ctrl.systems || '').split(';').map(s => s.trim()).filter(Boolean);
                      return (
                        <div key={ctrl.id} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <span className="font-mono font-bold text-xs text-indigo-600 dark:text-indigo-400 shrink-0">{ctrl.id}</span>
                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{ctrl.title || ctrl.name}</span>
                                <span className="text-[10px] text-slate-400 font-mono shrink-0">({ctrl.scope})</span>
                              </div>
                              {ctrlSystems.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {ctrlSystems.map(sysTag => (
                                    <span key={sysTag} className="px-1.5 py-0.2 rounded text-[9px] font-mono font-medium bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                                      {sysTag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={docLink.officialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-accent"
                              title={`Government Published Document: ${docLink.officialTitle} (${docLink.gazetteNotification})`}
                            >
                              <Landmark className="w-3 h-3 text-accent" />
                              <span>{docLink.shortTitle}</span>
                              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                            </a>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setEditingItem(ctrl); setIsFormOpen(true); }}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                title="Edit Control"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteControl(ctrl.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600"
                                title="Delete Control"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 7. STATUTORY PROVISIONS & RULES */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'provisions' && (
              <div className="space-y-4">
                
                <div className="flex items-center justify-between gap-3">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search statutory provisions by citation, text, actor..."
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      className="w-full text-xs pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100"
                    />
                  </div>

                  <button
                    onClick={() => {
                      const nextNum = masterData.provisions.length + 1;
                      setEditingItem({
                        id: `ACT-${nextNum}`,
                        provision: `s.${nextNum}`,
                        actor: 'Data Fiduciary',
                        commencement: '2027-05-13',
                        trigger: 'Processing',
                        conditions: '',
                        exception: '',
                        exact_passage: '',
                        author_interpretation: '',
                        source_url: 'https://www.meity.gov.in',
                        domain_slug: 'scope',
                        domain: 'Scope & Territorial Perimeter',
                        isRule: false,
                        isSchedule: false
                      });
                      setIsFormOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shrink-0 shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>New Provision</span>
                  </button>
                </div>

                {isFormOpen && (
                  <form onSubmit={handleSaveProvision} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700 space-y-3">
                    <div className="font-bold text-xs text-slate-900 dark:text-slate-100">
                      {editingItem?.id ? `Edit Provision: ${editingItem.id}` : 'Create New Statutory Provision / Rule'}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Provision ID (e.g. ACT-103 / RULE-15)"
                        value={editingItem?.id || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, id: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-mono text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        required
                        placeholder="Citation (e.g. Section 10(3) / Rule 15)"
                        value={editingItem?.provision || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, provision: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="date"
                        value={editingItem?.commencement || '2027-05-13'}
                        onChange={(e) => setEditingItem({ ...editingItem, commencement: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-mono text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Statutory Actor"
                        value={editingItem?.actor || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, actor: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                      <input
                        type="text"
                        placeholder="Domain Slug (e.g. scope, security, breach)"
                        value={editingItem?.domain_slug || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, domain_slug: e.target.value, domain: e.target.value })}
                        className="text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      rows={3}
                      placeholder="Exact statutory passage from official Gazette..."
                      value={editingItem?.exact_passage || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, exact_passage: e.target.value })}
                      className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 font-serif text-slate-900 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setIsFormOpen(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 text-xs font-bold text-white bg-accent rounded-xl">Save Provision</button>
                    </div>
                  </form>
                )}

                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {masterData.provisions
                    .filter(p => !itemSearch || p.id.toLowerCase().includes(itemSearch.toLowerCase()) || p.provision.toLowerCase().includes(itemSearch.toLowerCase()) || (p.actor || '').toLowerCase().includes(itemSearch.toLowerCase()))
                    .map((prov) => {
                      const docLink = getGovernmentLinkage(prov, false);
                      return (
                        <div key={prov.id} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <span className="font-mono font-bold text-xs text-sky-600 dark:text-sky-400 shrink-0">{prov.id}</span>
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{prov.provision}</span>
                            <span className="text-[11px] text-slate-500 truncate">({prov.actor})</span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <a
                              href={docLink.officialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-accent"
                              title={`Government Published Document: ${docLink.officialTitle} (${docLink.gazetteNotification})`}
                            >
                              <Landmark className="w-3 h-3 text-accent" />
                              <span>{docLink.shortTitle}</span>
                              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                            </a>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setEditingItem(prov); setIsFormOpen(true); }}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                title="Edit Provision"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProvision(prov.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600"
                                title="Delete Provision"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* 8. FACTORY RESET */}
            {/* ------------------------------------------------------------- */}
            {activeTab === 'reset' && (
              <div className="space-y-6">
                
                {/* Reset Warning Card */}
                <div className="p-6 rounded-2xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold text-sm">
                    <RotateCcw className="w-5 h-5" />
                    <span>Restore Factory Canonical Baseline (15 Sep 2026)</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    Resetting master data will restore the canonical list of Statutory Actors (6 roles), Compliance Domains (15 taxonomies), Commencement Tranches (3 tranches), Control Matrix (54 standard controls), and Statutory Provisions (102 provisions & rules).
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    <strong>Note:</strong> All existing applicability decisions, custom evaluation facts, and owner assignments in your workspace will remain completely intact.
                  </p>
                  
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Reset master data to factory canonical baseline? Custom controls and provision edits will be reset to default.')) {
                          onResetToDefaults();
                          showSaveSuccess('Master data reset to factory defaults');
                        }
                      }}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold text-amber-900 dark:text-amber-200 bg-amber-200/80 dark:bg-amber-950/80 hover:bg-amber-300 transition-colors shadow-xs"
                    >
                      Reset Master Data to Factory Baseline
                    </button>
                  </div>
                </div>

              </div>
            )}

          </div>

        </div>

      </div>

      {/* ------------------------------------------------------------- */}
      {/* STATUTORY SCOPE EVALUATION REVIEW MODAL */}
      {/* ------------------------------------------------------------- */}
      {isScopeReviewOpen && scopeEvaluationReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-slate-50 to-indigo-50/30 dark:from-slate-800/80 dark:to-indigo-950/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Zap className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                      Automated Statutory Scope Applicability Review
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      {scopeEvaluationReport.total_evaluated} Items ({scopeEvaluationReport.controls_count} Controls + {scopeEvaluationReport.provisions_count} Rules/Provisions)
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Review how your corporate identity, SDF designation, children data, cross-border transfers, and registered consent managers map to obligations and statutory rules.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsScopeReviewOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary KPI Banner */}
            <div className="px-6 py-3.5 bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-200/80 dark:border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
              <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Evaluated</div>
                  <div className="text-lg font-black text-slate-900 dark:text-slate-100 font-mono">
                    {scopeEvaluationReport.total_evaluated}
                  </div>
                </div>
                <Layers className="w-5 h-5 text-slate-400" />
              </div>

              <div className="p-3 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Applicable in Scope</div>
                  <div className="text-lg font-black text-emerald-700 dark:text-emerald-400 font-mono">
                    {scopeEvaluationReport.applicable_count}
                  </div>
                </div>
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>

              <div className="p-3 rounded-xl bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-rose-700 dark:text-rose-300 uppercase tracking-wider">Not Applicable / Relieved</div>
                  <div className="text-lg font-black text-rose-700 dark:text-rose-400 font-mono">
                    {scopeEvaluationReport.not_applicable_count}
                  </div>
                </div>
                <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>

              <div className="p-3 rounded-xl bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Status Updates (Diff)</div>
                  <div className="text-lg font-black text-amber-700 dark:text-amber-400 font-mono">
                    {scopeEvaluationReport.changed_count}
                  </div>
                </div>
                <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>

            {/* Applied Legal Triggers Summary Card */}
            <div className="px-6 py-3 bg-indigo-50/30 dark:bg-indigo-950/10 border-b border-indigo-100 dark:border-indigo-900/40 shrink-0">
              <div className="text-[11px] font-bold text-indigo-950 dark:text-indigo-300 mb-2 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <span>Statutory Scope Profile Grounding:</span>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium border ${
                  isSdfInput
                    ? 'bg-amber-100/80 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-800'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                }`}>
                  <strong>SDF s.10:</strong> {isSdfInput ? 'Designated SDF (OBL-33..36 & s.10/r.13 Mandatory)' : 'Non-SDF (OBL-33..36 & s.10/r.13 Relieved)'}
                </span>

                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium border ${
                  processesChildrenInput || processesDisabilitiesInput
                    ? 'bg-rose-100/80 dark:bg-rose-900/40 text-rose-900 dark:text-rose-200 border-rose-300 dark:border-rose-800'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                }`}>
                  <strong>Children s.9:</strong> {processesChildrenInput ? 'Children Data (OBL-24..31 & s.9/r.10-12 Active)' : 'No Children Data (OBL-24..31 & s.9/r.10-12 Relieved)'}
                </span>

                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium border ${
                  hasCrossBorderInput
                    ? 'bg-sky-100/80 dark:bg-sky-900/40 text-sky-900 dark:text-sky-200 border-sky-300 dark:border-sky-800'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                }`}>
                  <strong>Cross-Border s.16:</strong> {hasCrossBorderInput ? 'Active (OBL-22, 43, 44 & s.16/r.15 In-Scope)' : 'Domestic Only (OBL-22, 43, 44 & s.16/r.15 Relieved)'}
                </span>

                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium border ${
                  usesConsentManagersInput
                    ? 'bg-purple-100/80 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 border-purple-300 dark:border-purple-800'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                }`}>
                  <strong>Consent Managers s.6(9):</strong> {usesConsentManagersInput ? 'Active (OBL-46, 47 & s.6(9)/r.4 In-Scope)' : 'Direct Consent (OBL-46, 47 & s.6(9)/r.4 Relieved)'}
                </span>

                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                  <strong>Entity Type:</strong> {entityTypeInput}
                </span>
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Target Type Filter */}
                <div className="flex items-center gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl">
                  {[
                    { id: 'all', label: `All (${scopeEvaluationReport.total_evaluated})` },
                    { id: 'control', label: `Controls (${scopeEvaluationReport.controls_count})` },
                    { id: 'provision', label: `Rules (${scopeEvaluationReport.provisions_count})` },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setScopeFilterTargetType(t.id as any)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        scopeFilterTargetType === t.id
                          ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Status Filter */}
                {[
                  { id: 'all', label: 'All Statuses' },
                  { id: 'changed', label: `Changes (${scopeEvaluationReport.changed_count})` },
                  { id: 'not_applicable', label: `Not Applicable (${scopeEvaluationReport.not_applicable_count})` },
                  { id: 'applicable', label: `Applicable (${scopeEvaluationReport.applicable_count})` },
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setScopeFilterStatus(f.id as any)}
                    className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      scopeFilterStatus === f.id
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-[240px]">
                <select
                  value={scopeFilterCategory}
                  onChange={(e) => setScopeFilterCategory(e.target.value)}
                  className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500"
                >
                  <option value="all">All Categories</option>
                  <option value="SDF">Significant Data Fiduciary (s.10)</option>
                  <option value="CHILDREN_DATA">Children's Personal Data (s.9)</option>
                  <option value="CROSS_BORDER">Cross-Border Transfers (s.16)</option>
                  <option value="CONSENT_MANAGERS">Consent Managers (s.6(9))</option>
                  <option value="STATE_ENTITY">State Instrumentality (r.5)</option>
                  <option value="STATUTORY_EXCEPTION">Statutory Exceptions (s.17)</option>
                  <option value="CORE_FIDUCIARY">Baseline Enterprise Duties</option>
                </select>

                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search controls, rules & provisions..."
                    value={scopeSearchQuery}
                    onChange={(e) => setScopeSearchQuery(e.target.value)}
                    className="w-full text-xs pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Evaluated Obligations & Provisions List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {filteredScopeItems.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No obligations or statutory rules match the current filters.
                </div>
              ) : (
                filteredScopeItems.map(item => (
                  <div
                    key={item.target_id}
                    className={`p-4 rounded-2xl border transition-all ${
                      item.recommended_status === 'NOT_APPLICABLE'
                        ? 'bg-rose-50/30 dark:bg-rose-950/10 border-rose-200/80 dark:border-rose-900/40'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-black text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800">
                            {item.target_id}
                          </span>
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md uppercase border ${
                            item.target_type === 'control'
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                              : 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800'
                          }`}>
                            {item.target_type === 'control' ? 'Control' : (item.provision_ref?.startsWith('r.') ? 'Rule' : 'Section')}
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {item.domain}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                            {item.category_label}
                          </span>
                          <span className="text-[10px] font-mono text-slate-500 font-semibold">
                            {item.statutory_ground}
                          </span>
                        </div>

                        <h5 className="text-xs font-bold text-slate-900 dark:text-slate-100 pt-0.5">
                          {item.title}
                        </h5>

                        <p className="text-[11px] text-slate-600 dark:text-slate-300">
                          {item.rationale}
                        </p>

                        <div className="pt-1 text-[10px] font-mono text-slate-400">
                          Trigger: <span className="text-slate-600 dark:text-slate-300">{item.trigger_reason}</span>
                        </div>
                      </div>

                      {/* Target Status & Diff Badge */}
                      <div className="flex sm:flex-col items-end justify-between sm:justify-start gap-2 shrink-0">
                        <div className="flex items-center gap-1.5">
                          {item.is_changed && (
                            <span className="text-[10px] font-mono line-through text-slate-400 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                              {item.current_status}
                            </span>
                          )}
                          <span className={`px-2.5 py-1 rounded-xl text-xs font-black font-mono flex items-center gap-1 shadow-xs ${
                            item.recommended_status === 'APPLICABLE'
                              ? 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                              : 'bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-700'
                          }`}>
                            {item.recommended_status === 'APPLICABLE' ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                            )}
                            <span>{item.recommended_status}</span>
                          </span>
                        </div>

                        {item.is_changed ? (
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Will be updated
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">Unchanged</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between gap-4 shrink-0">
              <button
                type="button"
                onClick={() => setIsScopeReviewOpen(false)}
                className="px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
              >
                Cancel / Back
              </button>

              <button
                type="button"
                onClick={handleConfirmApplyScopeRules}
                className="flex items-center gap-2 px-6 py-2.5 text-xs font-extrabold text-white bg-indigo-600 hover:bg-indigo-700 active:scale-95 rounded-xl transition-all shadow-lg shadow-indigo-600/30 cursor-pointer"
              >
                <Zap className="w-4 h-4 fill-current" />
                <span>Confirm & Apply Scope Rules to Register ({scopeEvaluationReport.changed_count} updates)</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* STATUTORY SAFE HARBOURS EVALUATION REVIEW MODAL */}
      {/* ------------------------------------------------------------- */}
      {isExceptionsReviewOpen && exceptionsEvaluationReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-slate-50 to-amber-50/30 dark:from-slate-800/80 dark:to-amber-950/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-amber-600 text-white flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <ShieldAlert className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                      Statutory Safe Harbours Applicability Review
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                      Section 17 & Section 3(c)
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Preview mapped obligations to be marked as Not Applicable with formal statutory justification and approver sign-off.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsExceptionsReviewOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary KPI Banner */}
            <div className="px-6 py-3.5 bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-200/80 dark:border-slate-800 grid grid-cols-2 sm:grid-cols-5 gap-2.5 shrink-0">
              <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active Exceptions</div>
                  <div className="text-lg font-black text-slate-900 dark:text-slate-100 font-mono">
                    {exceptionsEvaluationReport.active_exceptions_count}
                  </div>
                </div>
                <ShieldCheck className="w-5 h-5 text-amber-500" />
              </div>

              <div className="p-3 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider">Mapped Items</div>
                  <div className="text-lg font-black text-indigo-700 dark:text-indigo-400 font-mono">
                    {exceptionsEvaluationReport.total_mapped_obligations_count}
                  </div>
                </div>
                <Layers className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>

              <div className="p-3 rounded-xl bg-purple-50/60 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider">Controls</div>
                  <div className="text-lg font-black text-purple-700 dark:text-purple-400 font-mono">
                    {exceptionsEvaluationReport.unique_controls_count}
                  </div>
                </div>
                <CheckCircle2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>

              <div className="p-3 rounded-xl bg-sky-50/60 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-sky-700 dark:text-sky-300 uppercase tracking-wider">Rules / Sections</div>
                  <div className="text-lg font-black text-sky-700 dark:text-sky-400 font-mono">
                    {exceptionsEvaluationReport.unique_provisions_count}
                  </div>
                </div>
                <FileText className="w-5 h-5 text-sky-600 dark:text-sky-400" />
              </div>

              <div className="p-3 rounded-xl bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 flex items-center justify-between col-span-2 sm:col-span-1">
                <div>
                  <div className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Status Updates</div>
                  <div className="text-lg font-black text-amber-700 dark:text-amber-400 font-mono">
                    {exceptionsEvaluationReport.changed_count}
                  </div>
                </div>
                <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Target Type Filter Tabs */}
                <div className="flex items-center gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl">
                  {[
                    { id: 'all', label: `All (${exceptionsEvaluationReport.total_mapped_obligations_count})` },
                    { id: 'control', label: `Controls (${exceptionsEvaluationReport.unique_controls_count})` },
                    { id: 'provision', label: `Rules (${exceptionsEvaluationReport.unique_provisions_count})` },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setExceptionsFilterTargetType(t.id as any)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        exceptionsFilterTargetType === t.id
                          ? 'bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setExceptionsFilterChangedOnly(false)}
                  className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    !exceptionsFilterChangedOnly
                      ? 'bg-amber-600 text-white shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  All Statuses
                </button>
                <button
                  type="button"
                  onClick={() => setExceptionsFilterChangedOnly(true)}
                  className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    exceptionsFilterChangedOnly
                      ? 'bg-amber-600 text-white shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  Changes Only ({exceptionsEvaluationReport.changed_count})
                </button>
              </div>

              <div className="flex items-center gap-2 flex-1 sm:flex-initial min-w-[240px]">
                <select
                  value={exceptionsFilterExceptionId}
                  onChange={(e) => setExceptionsFilterExceptionId(e.target.value)}
                  className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-amber-500"
                >
                  <option value="all">All Active Exceptions</option>
                  {(masterData.exceptions || []).filter(e => e.status === 'ACTIVE').map(e => (
                    <option key={e.id} value={e.id}>{e.id} - {e.statutory_ground} ({e.title.slice(0, 30)}...)</option>
                  ))}
                </select>

                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search controls, rules & provisions..."
                    value={exceptionsSearchQuery}
                    onChange={(e) => setExceptionsSearchQuery(e.target.value)}
                    className="w-full text-xs pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>
            </div>

            {/* Evaluated Obligations & Rules List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {filteredExceptionItems.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No mapped obligations or rules match the current filter.
                </div>
              ) : (
                filteredExceptionItems.map((item, idx) => (
                  <div
                    key={`${item.target_id}_${item.exception_id}_${idx}`}
                    className="p-4 rounded-2xl bg-rose-50/30 dark:bg-rose-950/10 border border-rose-200/80 dark:border-rose-900/40 transition-all space-y-2"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-black text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-lg bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800">
                            {item.target_id}
                          </span>
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md uppercase border ${
                            item.target_type === 'control'
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                              : 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800'
                          }`}>
                            {item.target_type === 'control' ? 'Control' : (item.provision_ref?.startsWith('r.') ? 'Rule' : 'Section')}
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {item.domain}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                            {item.exception_id} • {item.statutory_ground}
                          </span>
                        </div>

                        <h5 className="text-xs font-bold text-slate-900 dark:text-slate-100 pt-0.5">
                          {item.title}
                        </h5>

                        <p className="text-[11px] text-slate-600 dark:text-slate-300">
                          <strong className="text-slate-700 dark:text-slate-200 mr-1">Statutory Exemption:</strong>
                          {item.exception_title} — {item.justification}
                        </p>

                        <div className="pt-1 flex flex-wrap items-center gap-3 text-[10px] font-mono text-slate-500 dark:text-slate-400">
                          <span>Approved By: <strong className="text-slate-700 dark:text-slate-300">{item.approved_by}</strong></span>
                          <span>Approval Date: <strong>{item.approval_date}</strong></span>
                        </div>
                      </div>

                      {/* Target Status & Diff Badge */}
                      <div className="flex sm:flex-col items-end justify-between sm:justify-start gap-2 shrink-0">
                        <div className="flex items-center gap-1.5">
                          {item.is_changed && (
                            <span className="text-[10px] font-mono line-through text-slate-400 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                              {item.current_status}
                            </span>
                          )}
                          <span className="px-2.5 py-1 rounded-xl text-xs font-black font-mono flex items-center gap-1 shadow-xs bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-700">
                            <AlertCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                            <span>NOT_APPLICABLE</span>
                          </span>
                        </div>

                        {item.is_changed ? (
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Will be updated
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">Already Not Applicable</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between gap-4 shrink-0">
              <button
                type="button"
                onClick={() => setIsExceptionsReviewOpen(false)}
                className="px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
              >
                Cancel / Back
              </button>

              <button
                type="button"
                onClick={handleConfirmApplyExceptions}
                className="flex items-center gap-2 px-6 py-2.5 text-xs font-extrabold text-white bg-amber-600 hover:bg-amber-700 active:scale-95 rounded-xl transition-all shadow-lg shadow-amber-600/30 cursor-pointer"
              >
                <Zap className="w-4 h-4 fill-current" />
                <span>Confirm & Apply Safe Harbours to Register ({exceptionsEvaluationReport.changed_count} updates)</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
